import axios from "axios";

// Global Variables
import { ApiReqBaseUrl, resolveApiUrl } from "../assets/GlobalVariables";
import {
  performGetFetch,
  performPostFetch,
  performPostFetchThrowError,
} from "./fetchService";
import { getSessionId, getUserDetails } from "./authService";
import { notifyIfInvalidSession, triggerInvalidSessionLogout } from "./sessionInvalidation";
import { getUserInfo } from "./localStorage";
import { normalizeSearchFlightsPayloadForLds } from "../locations/searchFlightsPayloadNormalize.js";
import {
  summarizeDestinationEntity,
  traceDestinationSearch,
} from "../utils/destinationSearchEntity";
import {
  resolveLdsOrdersFetchDecision,
  emptyAxiosOrdersListEnvelope,
} from "../features/orders/ldsOrdersEntitlementGuard.js";
import { resolveLdsOrdersListRequestScope } from "../features/orders/ldsOrdersListScope.js";
import { buildWalletSummary } from "../utils/buildWalletSummary";
import { traceDashboardHeavyApiCall } from "../utils/dashboardHeavyApiTrace";
import { getTourInfo as getOrganizedTourInfoFromModule } from "../modules/organizedTours/api/tourInfoClient";
import { stripPublicSiteTokenFromLdsInventorySearchBody } from "../utils/attachPublicSiteTokenToLdsSearchBody";
import { sanitizeLdsSearchRequestBody } from "../contexts/SearchContextHelpers";

/**
 * Headline available wallet for an agent — same SSOT as Finance/Dashboard (no possibleProfit + row-sum).
 */
function agentAvailableWalletHeadlineFromResponses(financeData, availableCommissionResponse) {
  const layer = financeData?.data ?? financeData;
  const ppRaw = layer?.possibleProfit ?? layer?.data?.possibleProfit ?? null;
  return buildWalletSummary({
    futureLines: [],
    historyLines: [],
    availableBuckets: availableCommissionResponse?.data ?? null,
    possibleProfit: ppRaw,
  }).availableTotal;
}

// --- Functions

/** Same precedence as authService.getSessionId(user first, then localStorage). */
function _resolveWalletSessionIdSync() {
  try {
    const u = getUserInfo();
    const fromUser = u?.sessionID ?? u?.sessionId;
    if (fromUser != null && String(fromUser).trim() !== "") {
      return String(fromUser).trim();
    }
  } catch {
    // ignore
  }
  if (typeof localStorage !== "undefined") {
    const ls = localStorage.getItem("sessionID");
    if (ls != null && String(ls).trim() !== "") {
      return String(ls).trim();
    }
  }
  return "";
}

/** Exported for orders dashboard tracing (must match _attachAgentSessionToBody). */
function resolveAgentPostSessionIdSync() {
  return _resolveWalletSessionIdSync();
}

/** Propagate logged-in agent session to LDS POST bodies so backend can enforce entitlements (e.g. organized_tours). */
const _attachAgentSessionToBody = (data) => {
  if (
    typeof localStorage === "undefined" ||
    data == null ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return data;
  }
  const sid = _resolveWalletSessionIdSync();
  if (sid === "") {
    return data;
  }
  return { ...data, sessionID: sid };
};

const hotelProfileDataRequestCache = new Map();
const HOTEL_PROFILE_DATA_CACHE_TTL_MS = 2 * 60 * 1000;

function stableHotelProfileCacheStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map(stableHotelProfileCacheStringify).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .filter((key) => key !== "session" && key !== "sessionID")
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableHotelProfileCacheStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function buildHotelProfileDataCacheKey(data) {
  const hotelId = data?.hotelID ?? data?.hotelId ?? data?.HotelId ?? data?.id ?? "unknown";
  return `${hotelId}:${stableHotelProfileCacheStringify(data || {})}`;
}

function _walletBearerHeaders() {
  const trimmed = _resolveWalletSessionIdSync();
  if (!trimmed) {
    return {};
  }
  return { Authorization: `Bearer ${trimmed}` };
}

/** Flask legacy routes often return json.dumps(...) as text; normalize for axios. */
function _normalizeWalletResponseData(maybe) {
  if (maybe == null) return maybe;
  if (typeof maybe === "string") {
    const t = maybe.trim();
    if (!t) return maybe;
    try {
      return JSON.parse(t);
    } catch {
      return maybe;
    }
  }
  return maybe;
}

function _parseWalletAxiosErrorData(data) {
  if (data == null) return null;
  if (typeof data === "string") {
    const t = data.trim();
    if (!t) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }
  if (typeof data === "object") return data;
  return null;
}

/** Backend wallet identity errors: clear stale client session instead of silent empty lists. */
function _reactToWalletIdentityHttpError(err) {
  void notifyIfInvalidSession({
    status: err?.response?.status,
    data: err?.response?.data,
    url: err?.config?.url,
  });
  const st = err?.response?.status;
  const payload = _parseWalletAxiosErrorData(err?.response?.data);
  const code = payload && payload.error;
  if (st === 403 && code === "email_session_mismatch") {
    console.warn("[WALLET_IDENTITY_LOGOUT]", { status: st, code });
    void triggerInvalidSessionLogout();
  }
}

// -- Finance And Order - API Requests

// Get finance data
const getFinanceData = async (data) => {
  traceDashboardHeavyApiCall({
    endpoint: "/getBasicFinancials",
    reason: "getFinanceData→performPostFetch",
  });
  const res = await performPostFetch(
    resolveApiUrl("/getBasicFinancials"),
    _attachAgentSessionToBody(data),
    "אירעה שגיאה בשליפת המידע הפיננסי",
    { headers: _walletBearerHeaders() }
  );
  const resDataBeforeNormalize = res?.data;
  if (res && res.data !== undefined) {
    res.data = _normalizeWalletResponseData(res.data);
  }
  if (
    res?.status === 401 &&
    res?.data &&
    res.data.error === "session_invalid_or_inactive"
  ) {
    void notifyIfInvalidSession({
      status: res.status,
      data: res.data,
      url: resolveApiUrl("/getBasicFinancials"),
    });
  }
  if (
    res?.status === 403 &&
    res?.data &&
    res.data.error === "email_session_mismatch"
  ) {
    void triggerInvalidSessionLogout();
  }
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    const normalized = res?.data;
    console.log("[WALLET_GET_BASIC_FINANCIALS_RESPONSE]", {
      rawResponse: res,
      resData: resDataBeforeNormalize,
      "typeof res.data": typeof resDataBeforeNormalize,
      normalizedData: normalized,
      futureCommission: normalized?.futureCommission,
      possibleProfit: normalized?.possibleProfit,
    });
  }
  return res;
};

// Get orders data
const getOrders = async (data) => {
  // Create API request for orders data
  try {
    return await axios.post(`${ApiReqBaseUrl}/getUpcomingOrders`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Get new orders since last update
const getOrdersSinceLastUpdate = async (lastUpdateTime) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/getOrdersSinceLastUpdate`, {
      lastUpdateTime: lastUpdateTime,
    });
  } catch (err) {
    console.log("Error fetching orders since last update:", err);
    return { data: [] };
  }
};

const getFutureOrdersCommission = async (data) => {
  try {
    traceDashboardHeavyApiCall({
      endpoint: "/futureOrdersCommission",
      reason: "getFutureOrdersCommission→axios.post",
    });
    const res = await axios.post(
      resolveApiUrl("/futureOrdersCommission"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
    const normalized = _normalizeWalletResponseData(res.data);
    return { ...res, data: normalized };
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    console.warn("[WALLET_COMMISSION_AXIOS_ERR]", {
      endpoint: "futureOrdersCommission",
      message: err?.message,
      status: err?.response?.status,
    });
    return { data: [] };
  }
};

export const getAvailableOrdersCommission = async (data) => {
  try {
    traceDashboardHeavyApiCall({
      endpoint: "/availableOrdersCommission",
      reason: "getAvailableOrdersCommission→axios.post",
    });
    const res = await axios.post(
      resolveApiUrl("/availableOrdersCommission"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
    const normalized = _normalizeWalletResponseData(res.data);
    return { ...res, data: normalized };
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    console.warn("[WALLET_COMMISSION_AXIOS_ERR]", {
      endpoint: "availableOrdersCommission",
      message: err?.message,
      status: err?.response?.status,
    });
    return { data: [] };
  }
};

export const getOrdersCommissionHistory = async (data) => {
  try {
    const res = await axios.post(
      resolveApiUrl("/ordersCommissionHistory"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
    const normalized = _normalizeWalletResponseData(res.data);
    return { ...res, data: normalized };
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    console.warn("[WALLET_COMMISSION_AXIOS_ERR]", {
      endpoint: "ordersCommissionHistory",
      message: err?.message,
      status: err?.response?.status,
    });
    return { data: [] };
  }
};

export const getAgentWithdrawalHistory = async (data) => {
  try {
    return await axios.post(
      resolveApiUrl("/agentWithdrawalHistory"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    console.warn("[WALLET_COMMISSION_AXIOS_ERR]", {
      endpoint: "agentWithdrawalHistory",
      message: err?.message,
      status: err?.response?.status,
    });
    throw err;
  }
};

export const getAdminActiveWithdrawalRequests = async (data = {}) => {
  try {
    return await axios.post(
      resolveApiUrl("/adminActiveWithdrawalRequests"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    console.warn("[WALLET_COMMISSION_AXIOS_ERR]", {
      endpoint: "adminActiveWithdrawalRequests",
      message: err?.message,
      status: err?.response?.status,
    });
    throw err;
  }
};

export const adminCloseFinanceRequest = async ({ reqNumber, reason, note }) => {
  try {
    return await axios.post(
      resolveApiUrl("/adminCloseFinanceRequest"),
      _attachAgentSessionToBody({ reqNumber, reason, note }),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
  } catch (err) {
    _reactToWalletIdentityHttpError(err);
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "שגיאה בסגירה תפעולית";
    const wrapped = new Error(msg);
    wrapped.response = err?.response;
    throw wrapped;
  }
};

export const fetchFinanceWithdrawalInvoiceBlob = async ({ email, reqNumber, disposition = "inline" }) => {
  const res = await axios.post(
    resolveApiUrl("/financeWithdrawalInvoicePreview"),
    _attachAgentSessionToBody({ email, reqNumber, disposition }),
    {
      headers: {
        Accept: "application/pdf, application/octet-stream, image/*",
        "Content-Type": "application/json; charset=utf-8",
        ..._walletBearerHeaders(),
      },
      responseType: "blob",
      withCredentials: true,
      validateStatus: (status) => status >= 200 && status < 500,
    }
  );
  const blob = res.data;
  const ct = String(res.headers?.["content-type"] || blob?.type || "").toLowerCase();
  const looksLikeErrorBody =
    res.status >= 400 || ct.includes("application/json") || ct.includes("text/html") || ct.includes("text/plain");
  if (looksLikeErrorBody) {
    let detail = `invoice_preview_http_${res.status || 0}`;
    try {
      const text = typeof blob?.text === "function" ? await blob.text() : "";
      if (text) {
        try {
          const parsed = JSON.parse(text);
          detail = parsed?.error || parsed?.message || detail;
        } catch {
          detail = text.slice(0, 200) || detail;
        }
      }
    } catch {
      /* ignore */
    }
    const err = new Error(detail);
    err.response = res;
    throw err;
  }
  if (!blob || blob.size === 0) {
    throw new Error("empty_invoice");
  }
  return blob;
};

export const buildFinanceWithdrawalInvoicePreviewUrl = ({ email, reqNumber, disposition = "inline" }) => {
  const params = new URLSearchParams({
    email: String(email || ""),
    reqNumber: String(reqNumber),
    disposition,
  });
  const sessionID = resolveAgentPostSessionIdSync();
  if (sessionID) params.set("sessionID", sessionID);
  return `${resolveApiUrl("/financeWithdrawalInvoicePreview")}?${params.toString()}`;
};

export const cancelOrderByOrderId = async (data) => {
  try {
    return await axios.post(
      `${ApiReqBaseUrl}/cancelOrder`,
      _attachAgentSessionToBody(data),
      { withCredentials: true }
    );
  } catch (err) {
    console.log({ err });
  }
};

const getOrdersByEmail = async (data) => {
  return performPostFetch(
    `${ApiReqBaseUrl}/getOrders`,
    _attachAgentSessionToBody(data)
  );
};

const getAdminHotelOrders = async (data, options = {}) => {
  return axios.post(
    resolveApiUrl("/api/admin/hotel-orders"),
    _attachAgentSessionToBody(data),
    {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json; charset=utf-8",
        ..._walletBearerHeaders(),
      },
      withCredentials: true,
      signal: options.signal,
    }
  );
};

const getFinancesByEmail = async (data) => {
  try {
    return await axios.post(
      resolveApiUrl("/getFinanceByEmail"),
      _attachAgentSessionToBody(data),
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json; charset=utf-8",
          ..._walletBearerHeaders(),
        },
        withCredentials: true,
      }
    );
  } catch (err) {
    console.log({ err });
  }
};

const approveFinanceRequest = async ({ email, reqNumber }) => {
  try {
    // Bearer session identifies the acting admin for the balance audit log.
    return await axios.get(
      `${ApiReqBaseUrl}/approveFinanceRequest/${email}/${reqNumber}`,
      { headers: { ..._walletBearerHeaders() }, withCredentials: true }
    );
  } catch (err) {
    console.log({ err });
  }
};

const declineFinanceRequest = async ({ email, reqNumber }) => {
  try {
    return await axios.get(
      `${ApiReqBaseUrl}/declineFinanceRequest/${email}/${reqNumber}`,
      { headers: { ..._walletBearerHeaders() }, withCredentials: true }
    );
  } catch (err) {
    console.log({ err });
  }
};

const setFinanceRequestComments = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/setFinanceRequestComments`, data);
  } catch (err) {
    console.log({ err });
  }
};

const getAllUsersEmail = async () => {
  return performGetFetch(
    `${ApiReqBaseUrl}/getAllAgentsEmails`,
    {},
    "אירעה שגיאה בשליפת האימיילים"
  );
};

// Admin Finance APIs
const getAllAgentsBalanceSummary = async () => {
  try {
    console.log("🔍 Starting getAllAgentsBalanceSummary...");

    // Get all agents emails first
    const agentsResponse = await getAllUsersEmail();
    console.log("📧 Agents response:", agentsResponse);

    // Check if agentsResponse is an array directly or has data property
    const agentsEmails = Array.isArray(agentsResponse)
      ? agentsResponse
      : agentsResponse?.data;

    if (!agentsEmails || agentsEmails.length === 0) {
      console.log("❌ No agents data found");
      return { data: [] };
    }

    console.log(`📊 Found ${agentsEmails.length} agents`);

    // For each agent, get their balance data
    const agentsBalanceData = [];

    // Use all agents for complete data
    const limitedAgents = agentsEmails;

    for (const agentEmail of limitedAgents) {
      try {
        console.log(`🔍 Processing agent: ${agentEmail}`);

        // Check if agentEmail is a string, if not, try to extract email from object
        let actualEmail = agentEmail;
        if (typeof agentEmail !== "string") {
          // Try to find email property in the object
          if (agentEmail.email) {
            actualEmail = agentEmail.email;
            console.log(`✅ Found email in object: ${actualEmail}`);
          } else if (agentEmail.mail) {
            actualEmail = agentEmail.mail;
            console.log(`✅ Found mail in object: ${actualEmail}`);
          } else if (agentEmail.userEmail) {
            actualEmail = agentEmail.userEmail;
            console.log(`✅ Found userEmail in object: ${actualEmail}`);
          } else {
            console.log(
              `⚠️ Skipping agent - no email found in object:`,
              agentEmail
            );
            continue;
          }
        }

        // Get basic financials for this agent
        const financeData = await getFinanceData({ email: actualEmail });
        const availableCommission = await getAvailableOrdersCommission({
          email: actualEmail,
        });

        const totalBalance = agentAvailableWalletHeadlineFromResponses(
          financeData,
          availableCommission
        );

        console.log(`💵 Total balance for ${actualEmail}: ${totalBalance}`);

        agentsBalanceData.push({
          id: actualEmail,
          name: actualEmail.split("@")[0],
          email: actualEmail,
          balance: totalBalance,
          pendingRequests: 0,
          lastWithdrawal: new Date().toISOString().split("T")[0],
          status: "active",
        });
      } catch (error) {
        console.error(`❌ Error getting data for agent ${actualEmail}:`, error);
        // Add agent with zero balance if there's an error
        if (typeof actualEmail === "string") {
          agentsBalanceData.push({
            id: actualEmail,
            name: actualEmail.split("@")[0],
            email: actualEmail,
            balance: 0,
            pendingRequests: 0,
            lastWithdrawal: new Date().toISOString().split("T")[0],
            status: "active",
          });
        }
      }
    }

    console.log("✅ Final agents balance data:", agentsBalanceData);
    return { data: agentsBalanceData };
  } catch (error) {
    console.error("❌ Error in getAllAgentsBalanceSummary:", error);
    return { data: [] };
  }
};

const getAllAgentsFutureBalanceSummary = async () => {
  try {
    console.log("🔍 Starting getAllAgentsFutureBalanceSummary...");

    // Get all agents emails first
    const agentsResponse = await getAllUsersEmail();

    // Check if agentsResponse is an array directly or has data property
    const agentsEmails = Array.isArray(agentsResponse)
      ? agentsResponse
      : agentsResponse?.data;

    if (!agentsEmails || agentsEmails.length === 0) {
      return { data: [] };
    }

    // For each agent, get their future balance data
    const agentsFutureData = [];

    // Use all agents for complete data
    const limitedAgents = agentsEmails;

    for (const agentEmail of limitedAgents) {
      try {
        console.log(`🔍 Processing future data for agent: ${agentEmail}`);

        // Check if agentEmail is a string, if not, try to extract email from object
        let actualEmail = agentEmail;
        if (typeof agentEmail !== "string") {
          // Try to find email property in the object
          if (agentEmail.email) {
            actualEmail = agentEmail.email;
            console.log(`✅ Found email in object: ${actualEmail}`);
          } else if (agentEmail.mail) {
            actualEmail = agentEmail.mail;
            console.log(`✅ Found mail in object: ${actualEmail}`);
          } else if (agentEmail.userEmail) {
            actualEmail = agentEmail.userEmail;
            console.log(`✅ Found userEmail in object: ${actualEmail}`);
          } else {
            console.log(
              `⚠️ Skipping agent - no email found in object:`,
              agentEmail
            );
            continue;
          }
        }

        // Get current balance
        const financeData = await getFinanceData({ email: actualEmail });
        const availableCommission = await getAvailableOrdersCommission({
          email: actualEmail,
        });
        const futureCommission = await getFutureOrdersCommission({
          email: actualEmail,
        });

        const currentBalance = agentAvailableWalletHeadlineFromResponses(
          financeData,
          availableCommission
        );

        let futureBalance = 0;

        if (futureCommission?.data && Array.isArray(futureCommission.data)) {
          futureBalance = futureCommission.data.reduce(
            (sum, order) => sum + (order.commission || 0),
            0
          );
        }

        const totalFutureBalance = currentBalance + futureBalance;

        // Calculate growth rate
        const growthRate =
          currentBalance > 0 ? (futureBalance / currentBalance) * 100 : 0;

        agentsFutureData.push({
          id: actualEmail,
          name: actualEmail.split("@")[0],
          email: actualEmail,
          currentBalance: currentBalance,
          futureBalance: totalFutureBalance,
          growthRate: Math.min(growthRate, 100), // Cap at 100%
          expectedOrders: (futureCommission?.data || []).length,
          avgOrderValue:
            futureCommission?.data?.length > 0
              ? futureCommission.data.reduce(
                  (sum, order) => sum + (order.commission || 0),
                  0
                ) / futureCommission.data.length
              : 0,
          nextMonthProjection: totalFutureBalance * 1.2, // Mock projection
          status:
            growthRate > 20
              ? "growing"
              : growthRate > 0
                ? "stable"
                : "declining",
        });
      } catch (error) {
        console.error(
          `❌ Error getting future data for agent ${actualEmail}:`,
          error
        );
        // Add agent with zero data if there's an error
        if (typeof actualEmail === "string") {
          agentsFutureData.push({
            id: actualEmail,
            name: actualEmail.split("@")[0],
            email: actualEmail,
            currentBalance: 0,
            futureBalance: 0,
            growthRate: 0,
            expectedOrders: 0,
            avgOrderValue: 0,
            nextMonthProjection: 0,
            status: "stable",
          });
        }
      }
    }

    console.log("✅ Final agents future data:", agentsFutureData);
    return { data: agentsFutureData };
  } catch (error) {
    console.error("❌ Error in getAllAgentsFutureBalanceSummary:", error);
    return { data: [] };
  }
};

// Get all orders with detailed finance information for admin (with progress tracking)
const getAllOrdersWithFinanceDetailsWithProgress = async (progressCallback) => {
  try {
    console.log("🔍 Starting getAllOrdersWithFinanceDetails...");

    // Get all agents
    const agentsResponse = await getAllUsersEmail();
    console.log("📧 Agents response:", agentsResponse);

    // Handle both direct array and object with data property
    const agentsEmails = Array.isArray(agentsResponse)
      ? agentsResponse
      : agentsResponse?.data || [];

    if (!agentsEmails || agentsEmails.length === 0) {
      console.log("❌ No agents data found");
      return { data: [] };
    }

    console.log(`📊 Found ${agentsEmails.length} agents`);

    // Initialize progress
    progressCallback({
      current: 0,
      total: agentsEmails.length,
      percentage: 0,
      currentAgent: "",
      status: "מושך רשימת סוכנים...",
    });

    const allOrdersData = [];

    // Iterate through all agents and get their orders
    for (let i = 0; i < agentsEmails.length; i++) {
      const agentEmail = agentsEmails[i];
      try {
        console.log("🔍 Processing agent:", agentEmail);

        // Update progress
        progressCallback({
          current: i + 1,
          total: agentsEmails.length,
          percentage: Math.round(((i + 1) / agentsEmails.length) * 100),
          currentAgent: agentEmail,
          status: `מעבד סוכן ${i + 1} מתוך ${agentsEmails.length}...`,
        });

        // Handle case where agentEmail might be an object
        let actualEmail;
        if (typeof agentEmail === "string") {
          actualEmail = agentEmail;
        } else if (typeof agentEmail === "object" && agentEmail !== null) {
          // Try to extract email from common properties
          actualEmail =
            agentEmail.email ||
            agentEmail.mail ||
            agentEmail.userEmail ||
            agentEmail.emailAddress;
          console.log("📧 Extracted email from object:", actualEmail);
        }

        if (!actualEmail || typeof actualEmail !== "string") {
          console.log("⚠️ Skipping agent - not a string:", agentEmail);
          continue;
        }

        console.log("✅ Processing agent email:", actualEmail);

        // Update status for current agent
        progressCallback({
          current: i + 1,
          total: agentsEmails.length,
          percentage: Math.round(((i + 1) / agentsEmails.length) * 100),
          currentAgent: actualEmail,
          status: `מושך הזמנות עבור ${actualEmail}...`,
        });

        // Get orders for this agent
        const ordersResponse = await getOrdersByEmail({ email: actualEmail });
        console.log(`📋 Orders for ${actualEmail}:`, ordersResponse);

        const orders =
          ordersResponse?.data?.orders ||
          ordersResponse?.data ||
          ordersResponse ||
          [];

        if (!Array.isArray(orders)) {
          console.log(`⚠️ No valid orders data for ${actualEmail}`);
          continue;
        }

        // Update status for commissions
        progressCallback({
          current: i + 1,
          total: agentsEmails.length,
          percentage: Math.round(((i + 1) / agentsEmails.length) * 100),
          currentAgent: actualEmail,
          status: `מושך נתוני עמלות עבור ${actualEmail}...`,
        });

        // Get available commission data for this agent
        const availableCommissionResponse = await getAvailableOrdersCommission({
          email: actualEmail,
        });
        const availableCommissions = availableCommissionResponse?.data || [];

        // Get future commission data for this agent
        const futureCommissionResponse = await getFutureOrdersCommission({
          email: actualEmail,
        });
        const futureCommissions = futureCommissionResponse?.data || [];

        // Get packages orders for this agent
        let packagesOrders = [];
        try {
          const packagesResponse = await getLDSOrders(
            actualEmail,
            "FlightsHotel"
          );
          packagesOrders = packagesResponse?.data || [];
          console.log(`📦 Packages for ${actualEmail}:`, packagesOrders.length);
        } catch (error) {
          console.log(`⚠️ No packages found for ${actualEmail}`);
        }

        // Get organized tours for this agent
        let organizedTours = [];
        try {
          const toursResponse = await getLDSOrders(
            actualEmail,
            "OrganizedTour"
          );
          organizedTours = toursResponse?.data || [];
          console.log(
            `🏛️ Organized tours for ${actualEmail}:`,
            organizedTours.length
          );
        } catch (error) {
          console.log(`⚠️ No organized tours found for ${actualEmail}`);
        }

        // Calculate agent name from email (before @)
        const agentName = actualEmail.split("@")[0];

        const totalOrders =
          orders.length + packagesOrders.length + organizedTours.length;
        console.log(
          `📋 Processing ${totalOrders} total orders for agent ${actualEmail} (${orders.length} hotels, ${packagesOrders.length} packages, ${organizedTours.length} tours)`
        );

        // Update status for processing orders
        progressCallback({
          current: i + 1,
          total: agentsEmails.length,
          percentage: Math.round(((i + 1) / agentsEmails.length) * 100),
          currentAgent: actualEmail,
          status: `מעבד ${totalOrders} הזמנות עבור ${actualEmail}...`,
        });

        // Process hotel orders
        orders.forEach((order, index) => {
          // Extract real data from hotel order
          const orderId =
            order.orderid || order.id || `ORD-${Date.now()}-${index}`;
          const hotelName =
            order.hotelname || order.hotelName || `מלון ${index + 1}`;
          const checkInDate =
            order.checkin || order.checkIn || order.checkInDate;
          const checkOutDate =
            order.checkout || order.checkOut || order.checkOutDate;
          const creationDate =
            order.creationdate ||
            order.orderDate ||
            order.createdAt ||
            new Date().toISOString().split("T")[0];

          // Calculate nights between check-in and check-out
          let calculatedNights = 1;
          if (checkInDate && checkOutDate) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            calculatedNights = Math.max(
              1,
              Math.ceil(timeDiff / (1000 * 3600 * 24))
            );
          }

          // Get price details from order data
          const finalPrice =
            order.pricetotal ||
            order.total_price ||
            order.totalPrice ||
            order.amount ||
            0;

          // Get supplier price directly from order data if available
          const supplierPrice =
            order.supplier_price ||
            order.supplierPrice ||
            order.base_price ||
            order.basePrice ||
            Math.round(finalPrice * 0.85);

          // Calculate commission (difference between final price and supplier price)
          const agentCommissionAmount = finalPrice - supplierPrice;
          const agentCommissionPercentage =
            finalPrice > 0
              ? Math.round((agentCommissionAmount / finalPrice) * 100)
              : 0;

          // Get handling fee from order data or calculate as markup
          const handlingFee =
            order.handling_fee ||
            order.handlingFee ||
            order.handle_fee ||
            order.handleFee ||
            0;
          const markupAmount =
            handlingFee > 0 ? handlingFee : agentCommissionAmount;
          const markupPercentage =
            supplierPrice > 0
              ? Math.round((markupAmount / supplierPrice) * 100)
              : 0;

          // Company profit = 0 for hotels (agent keeps all markup)
          const companyProfit = 0;

          // Determine real status based on order data
          let realStatus = "pending";
          if (order.cancelled) {
            realStatus = "cancelled";
          } else if (order.paid) {
            realStatus = "confirmed";
          } else if (order.status) {
            const statusLower = order.status.toLowerCase();
            if (
              statusLower.includes("confirm") ||
              statusLower.includes("approved") ||
              statusLower.includes("booked")
            ) {
              realStatus = "confirmed";
            } else if (
              statusLower.includes("cancel") ||
              statusLower.includes("declined")
            ) {
              realStatus = "cancelled";
            }
          }

          // Determine product type based on order data
          let productType = "hotel"; // default
          if (order.package_type || order.deal_type) {
            const type = (order.package_type || order.deal_type).toLowerCase();
            if (type.includes("package") || type.includes("חבילה")) {
              productType = "package";
            } else if (type.includes("organized") || type.includes("מאורגן")) {
              productType = "organized";
            }
          }

          allOrdersData.push({
            id: `${actualEmail}-${order.orderid || order.id || index}`,
            orderId:
              order.orderid ||
              order.orderId ||
              order.id ||
              `ORD-${Date.now()}-${index}`,
            productType: productType,
            agentName: agentName,
            agentEmail: actualEmail,
            clientName:
              order.fullname ||
              order.clientName ||
              order.guestName ||
              order.customerName ||
              order.travelerName ||
              `לקוח ${index + 1}`,
            hotelName:
              order.hotelname ||
              order.hotelName ||
              order.propertyName ||
              order.accommodationName ||
              order.name ||
              `מלון ${index + 1}`,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            nights: order.nights || calculatedNights,
            supplierPrice: Math.round(supplierPrice),
            markupPercentage: markupPercentage,
            markupAmount: Math.round(markupAmount),
            agentCommissionPercentage: agentCommissionPercentage,
            agentCommissionAmount: Math.round(agentCommissionAmount),
            finalPrice: Math.round(finalPrice),
            companyProfit: Math.round(companyProfit),
            status: realStatus,
            orderDate:
              order.creationdate ||
              order.orderDate ||
              order.createdAt ||
              order.bookingDate ||
              order.created ||
              new Date().toISOString().split("T")[0],
            totalPrice: finalPrice,
            commission: agentCommissionAmount,
            clientPhone:
              order.phone ||
              order.clientPhone ||
              order.customerPhone ||
              order.travelerPhone ||
              "לא זמין",
          });
        });

        // Process package orders
        packagesOrders.forEach((packageOrder, index) => {
          const orderId =
            packageOrder.orderid ||
            packageOrder.id ||
            `PKG-${Date.now()}-${index}`;
          const packageName =
            packageOrder.package_name ||
            packageOrder.name ||
            packageOrder.title ||
            `חבילה ${index + 1}`;
          const checkInDate =
            packageOrder.start_date ||
            packageOrder.checkin ||
            packageOrder.checkIn ||
            packageOrder.checkInDate;
          const checkOutDate =
            packageOrder.end_date ||
            packageOrder.checkout ||
            packageOrder.checkOut ||
            packageOrder.checkOutDate;
          const creationDate =
            packageOrder.creation_date ||
            packageOrder.orderDate ||
            packageOrder.createdAt ||
            new Date().toISOString().split("T")[0];

          // Calculate nights
          let calculatedNights = 1;
          if (checkInDate && checkOutDate) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            calculatedNights = Math.max(
              1,
              Math.ceil(timeDiff / (1000 * 3600 * 24))
            );
          }

          // Get price details
          const finalPrice =
            packageOrder.total_price ||
            packageOrder.price ||
            packageOrder.amount ||
            0;
          const supplierPrice =
            packageOrder.supplier_price ||
            packageOrder.base_price ||
            Math.round(finalPrice * 0.75);
          const agentCommissionAmount = finalPrice - supplierPrice;
          const agentCommissionPercentage =
            finalPrice > 0
              ? Math.round((agentCommissionAmount / finalPrice) * 100)
              : 0;

          // Calculate markup
          const handlingFee =
            packageOrder.handling_fee || packageOrder.commission || 0;
          const markupAmount =
            handlingFee > 0 ? handlingFee : agentCommissionAmount;
          const markupPercentage =
            supplierPrice > 0
              ? Math.round((markupAmount / supplierPrice) * 100)
              : 0;

          // Company profit for packages
          const companyProfit = markupAmount - agentCommissionAmount;

          // Determine status
          let realStatus = "pending";
          if (packageOrder.cancelled) {
            realStatus = "cancelled";
          } else if (packageOrder.paid) {
            realStatus = "confirmed";
          } else if (packageOrder.status) {
            const statusLower = packageOrder.status.toLowerCase();
            if (
              statusLower.includes("confirm") ||
              statusLower.includes("approved") ||
              statusLower.includes("booked")
            ) {
              realStatus = "confirmed";
            } else if (
              statusLower.includes("cancel") ||
              statusLower.includes("declined")
            ) {
              realStatus = "cancelled";
            }
          }

          allOrdersData.push({
            id: `${actualEmail}-PKG-${packageOrder.orderid || packageOrder.id || index}`,
            orderId: orderId,
            productType: "package",
            agentName: agentName,
            agentEmail: actualEmail,
            clientName:
              packageOrder.client_name ||
              packageOrder.fullname ||
              packageOrder.customer_name ||
              `לקוח ${index + 1}`,
            hotelName: packageName,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            nights: packageOrder.nights || calculatedNights,
            supplierPrice: Math.round(supplierPrice),
            markupPercentage: markupPercentage,
            markupAmount: Math.round(markupAmount),
            agentCommissionPercentage: agentCommissionPercentage,
            agentCommissionAmount: Math.round(agentCommissionAmount),
            finalPrice: Math.round(finalPrice),
            companyProfit: Math.round(companyProfit),
            status: realStatus,
            orderDate: creationDate,
            totalPrice: finalPrice,
            commission: agentCommissionAmount,
            clientPhone:
              packageOrder.phone || packageOrder.client_phone || "לא זמין",
          });
        });

        // Process organized tour orders
        organizedTours.forEach((tourOrder, index) => {
          const orderId =
            tourOrder.orderid || tourOrder.id || `TOUR-${Date.now()}-${index}`;
          const tourName =
            tourOrder.tour_name ||
            tourOrder.name ||
            tourOrder.title ||
            `טיול מאורגן ${index + 1}`;
          const checkInDate =
            tourOrder.start_date ||
            tourOrder.checkin ||
            tourOrder.checkIn ||
            tourOrder.checkInDate;
          const checkOutDate =
            tourOrder.end_date ||
            tourOrder.checkout ||
            tourOrder.checkOut ||
            tourOrder.checkOutDate;
          const creationDate =
            tourOrder.creation_date ||
            tourOrder.orderDate ||
            tourOrder.createdAt ||
            new Date().toISOString().split("T")[0];

          // Calculate nights
          let calculatedNights = 1;
          if (checkInDate && checkOutDate) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            calculatedNights = Math.max(
              1,
              Math.ceil(timeDiff / (1000 * 3600 * 24))
            );
          }

          // Get price details
          const finalPrice =
            tourOrder.total_price || tourOrder.price || tourOrder.amount || 0;
          const supplierPrice =
            tourOrder.supplier_price ||
            tourOrder.base_price ||
            Math.round(finalPrice * 0.7);
          const agentCommissionAmount = finalPrice - supplierPrice;
          const agentCommissionPercentage =
            finalPrice > 0
              ? Math.round((agentCommissionAmount / finalPrice) * 100)
              : 0;

          // Calculate markup
          const handlingFee =
            tourOrder.handling_fee || tourOrder.commission || 0;
          const markupAmount =
            handlingFee > 0 ? handlingFee : agentCommissionAmount;
          const markupPercentage =
            supplierPrice > 0
              ? Math.round((markupAmount / supplierPrice) * 100)
              : 0;

          // Company profit for tours
          const companyProfit = markupAmount - agentCommissionAmount;

          // Determine status
          let realStatus = "pending";
          if (tourOrder.cancelled) {
            realStatus = "cancelled";
          } else if (tourOrder.paid) {
            realStatus = "confirmed";
          } else if (tourOrder.status) {
            const statusLower = tourOrder.status.toLowerCase();
            if (
              statusLower.includes("confirm") ||
              statusLower.includes("approved") ||
              statusLower.includes("booked")
            ) {
              realStatus = "confirmed";
            } else if (
              statusLower.includes("cancel") ||
              statusLower.includes("declined")
            ) {
              realStatus = "cancelled";
            }
          }

          allOrdersData.push({
            id: `${actualEmail}-TOUR-${tourOrder.orderid || tourOrder.id || index}`,
            orderId: orderId,
            productType: "organized",
            agentName: agentName,
            agentEmail: actualEmail,
            clientName:
              tourOrder.client_name ||
              tourOrder.fullname ||
              tourOrder.customer_name ||
              `לקוח ${index + 1}`,
            hotelName: tourName,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            nights: tourOrder.nights || calculatedNights,
            supplierPrice: Math.round(supplierPrice),
            markupPercentage: markupPercentage,
            markupAmount: Math.round(markupAmount),
            agentCommissionPercentage: agentCommissionPercentage,
            agentCommissionAmount: Math.round(agentCommissionAmount),
            finalPrice: Math.round(finalPrice),
            companyProfit: Math.round(companyProfit),
            status: realStatus,
            orderDate: creationDate,
            totalPrice: finalPrice,
            commission: agentCommissionAmount,
            clientPhone: tourOrder.phone || tourOrder.client_phone || "לא זמין",
          });
        });
      } catch (error) {
        console.error(
          `❌ Error getting orders for agent ${agentEmail}:`,
          error
        );
        continue;
      }
    }

    // Final progress update
    progressCallback({
      current: agentsEmails.length,
      total: agentsEmails.length,
      percentage: 100,
      currentAgent: "",
      status: "מסיים עיבוד נתונים...",
    });

    console.log("✅ Final orders with finance details:", allOrdersData);
    console.log(`📊 Total orders found: ${allOrdersData.length}`);

    return { data: allOrdersData };
  } catch (error) {
    console.error("❌ Error in getAllOrdersWithFinanceDetails:", error);
    return { data: [] };
  }
};

// Get all orders with detailed finance information for admin
const getAllOrdersWithFinanceDetails = async () => {
  try {
    console.log("🔍 Starting getAllOrdersWithFinanceDetails...");

    // Get all agents
    const agentsResponse = await getAllUsersEmail();
    console.log("📧 Agents response:", agentsResponse);

    // Handle both direct array and object with data property
    const agentsEmails = Array.isArray(agentsResponse)
      ? agentsResponse
      : agentsResponse?.data || [];

    if (!agentsEmails || agentsEmails.length === 0) {
      console.log("❌ No agents data found");
      return { data: [] };
    }

    console.log(`📊 Found ${agentsEmails.length} agents`);

    const allOrdersData = [];

    // Iterate through all agents and get their orders
    for (const agentEmail of agentsEmails) {
      try {
        console.log("🔍 Processing agent:", agentEmail);

        // Handle case where agentEmail might be an object
        let actualEmail;
        if (typeof agentEmail === "string") {
          actualEmail = agentEmail;
        } else if (typeof agentEmail === "object" && agentEmail !== null) {
          // Try to extract email from common properties
          actualEmail =
            agentEmail.email ||
            agentEmail.mail ||
            agentEmail.userEmail ||
            agentEmail.emailAddress;
          console.log("📧 Extracted email from object:", actualEmail);
        }

        if (!actualEmail || typeof actualEmail !== "string") {
          console.log("⚠️ Skipping agent - not a string:", agentEmail);
          continue;
        }

        console.log("✅ Processing agent email:", actualEmail);

        // Get orders for this agent
        const ordersResponse = await getOrdersByEmail({ email: actualEmail });
        console.log(`📋 Orders for ${actualEmail}:`, ordersResponse);

        const orders =
          ordersResponse?.data?.orders ||
          ordersResponse?.data ||
          ordersResponse ||
          [];

        if (!Array.isArray(orders)) {
          console.log(`⚠️ No valid orders data for ${actualEmail}`);
          continue;
        }

        // Get available commission data for this agent
        const availableCommissionResponse = await getAvailableOrdersCommission({
          email: actualEmail,
        });
        const availableCommissions = availableCommissionResponse?.data || [];

        // Get future commission data for this agent
        const futureCommissionResponse = await getFutureOrdersCommission({
          email: actualEmail,
        });
        const futureCommissions = futureCommissionResponse?.data || [];

        // Get packages orders for this agent
        let packagesOrders = [];
        try {
          const packagesResponse = await getLDSOrders(
            actualEmail,
            "FlightsHotel"
          );
          packagesOrders = packagesResponse?.data || [];
          console.log(`📦 Packages for ${actualEmail}:`, packagesOrders.length);
        } catch (error) {
          console.log(`⚠️ No packages found for ${actualEmail}`);
        }

        // Calculate agent name from email (before @)
        const agentName = actualEmail.split("@")[0];

        console.log(
          `📋 Processing ${orders.length} orders for agent ${actualEmail}`
        );

        // Process each order and add finance details
        orders.forEach((order, index) => {
          // Find matching commission data
          const availableCommission = availableCommissions.find(
            (comm) => comm.orderId === order.orderId || comm.id === order.id
          );
          const futureCommission = futureCommissions.find(
            (comm) => comm.orderId === order.orderId || comm.id === order.id
          );

          // Calculate financial details based on real order data
          const basePrice =
            order.total_price ||
            order.totalPrice ||
            order.amount ||
            order.price ||
            0;

          // Use real data when available, otherwise reasonable defaults
          const supplierPrice =
            basePrice > 0 ? Math.round(basePrice * 0.75) : 1500; // 75% of total price
          const markupPercentage = 15; // Standard 15% markup
          const markupAmount = Math.round(
            supplierPrice * (markupPercentage / 100)
          );
          const finalPrice = supplierPrice + markupAmount;

          // Calculate agent commission from available commission data
          const realCommission =
            availableCommission?.commission ||
            futureCommission?.commission ||
            0;
          const agentCommissionAmount =
            realCommission > 0 ? realCommission : Math.round(finalPrice * 0.08); // 8% default
          const agentCommissionPercentage =
            finalPrice > 0
              ? Math.round((agentCommissionAmount / finalPrice) * 100)
              : 8;

          const companyProfit = markupAmount - agentCommissionAmount;

          // Calculate nights between check-in and check-out
          let calculatedNights = 1;
          const checkInDate =
            order.checkin ||
            order.checkIn ||
            order.checkInDate ||
            order.startDate;
          const checkOutDate =
            order.checkout ||
            order.checkOut ||
            order.checkOutDate ||
            order.endDate;

          if (checkInDate && checkOutDate) {
            const checkIn = new Date(checkInDate);
            const checkOut = new Date(checkOutDate);
            const timeDiff = checkOut.getTime() - checkIn.getTime();
            calculatedNights = Math.max(
              1,
              Math.ceil(timeDiff / (1000 * 3600 * 24))
            );
          }

          // Determine real status based on order data
          let realStatus = "pending";
          if (order.cancelled) {
            realStatus = "cancelled";
          } else if (order.paid) {
            realStatus = "confirmed";
          } else if (order.status) {
            const statusLower = order.status.toLowerCase();
            if (
              statusLower.includes("confirm") ||
              statusLower.includes("approved") ||
              statusLower.includes("booked")
            ) {
              realStatus = "confirmed";
            } else if (
              statusLower.includes("cancel") ||
              statusLower.includes("declined")
            ) {
              realStatus = "cancelled";
            }
          }

          allOrdersData.push({
            id: `${actualEmail}-${order.orderid || order.id || index}`,
            orderId:
              order.orderid ||
              order.orderId ||
              order.id ||
              `ORD-${Date.now()}-${index}`,
            agentName: agentName,
            agentEmail: actualEmail,
            clientName:
              order.fullname ||
              order.clientName ||
              order.guestName ||
              order.customerName ||
              order.travelerName ||
              `לקוח ${index + 1}`,
            hotelName:
              order.hotelname ||
              order.hotelName ||
              order.propertyName ||
              order.accommodationName ||
              order.name ||
              `מלון ${index + 1}`,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            nights: order.nights || calculatedNights,
            supplierPrice: Math.round(supplierPrice),
            markupPercentage: markupPercentage,
            markupAmount: Math.round(markupAmount),
            agentCommissionPercentage: agentCommissionPercentage,
            agentCommissionAmount: Math.round(agentCommissionAmount),
            finalPrice: Math.round(finalPrice),
            companyProfit: Math.round(companyProfit),
            status: realStatus,
            orderDate:
              order.creationdate ||
              order.orderDate ||
              order.createdAt ||
              order.bookingDate ||
              order.created ||
              new Date().toISOString().split("T")[0],
            totalPrice: basePrice,
            commission: realCommission,
            hotelRating:
              order.rating || order.hotelRating || 4.0 + Math.random() * 1.0, // 4.0-5.0
            hotelStars:
              order.stars ||
              order.hotelStars ||
              Math.floor(Math.random() * 3) + 3, // 3-5 stars
          });
        });
      } catch (error) {
        console.error(
          `❌ Error getting orders for agent ${agentEmail}:`,
          error
        );
        continue;
      }
    }

    console.log("✅ Final orders with finance details:", allOrdersData);
    console.log(`📊 Total orders found: ${allOrdersData.length}`);

    return { data: allOrdersData };
  } catch (error) {
    console.error("❌ Error in getAllOrdersWithFinanceDetails:", error);
    return { data: [] };
  }
};

const getLDSOrders = async (
  agentId,
  dealType,
  page,
  size,
  filters = {},
  ldsEntitlementsCtx = undefined
) => {
  const listScope = resolveLdsOrdersListRequestScope(filters);
  if (
    ["FlightsHotel", "OrganizedTour", "Flights", "Flight"].indexOf(dealType) ===
    -1
  ) {
    throw new Error(
      'dealType must be one of "FlightsHotel", "OrganizedTour", "Flights", "Flight"'
    );
  }
  const endpoint = `${ApiReqBaseUrl}/getLDSOrdersByAgentId`;
  const decision = resolveLdsOrdersFetchDecision(
    dealType,
    ldsEntitlementsCtx && typeof ldsEntitlementsCtx === "object"
      ? ldsEntitlementsCtx
      : {},
    endpoint
  );
  if (decision.skipFetch) {
    return emptyAxiosOrdersListEnvelope(page, size);
  }
  const effectiveAgentId =
    listScope.agentId != null && String(listScope.agentId).trim() !== ""
      ? listScope.agentId
      : agentId;
  const body = {
    ...(effectiveAgentId != null && String(effectiveAgentId).trim() !== ""
      ? { agent_id: effectiveAgentId }
      : {}),
    scope: listScope.scope,
    ...(listScope.includeAllAgents ? { includeAllAgents: true } : {}),
    deal_type: dealType,
    ...(page != null ? { page } : {}),
    ...(size != null ? { size } : {}),
    ...(filters && typeof filters === "object" ? filters : {}),
  };
  let sid = null;
  try {
    sid = await getSessionId();
  } catch {
    sid = null;
  }
  const sidTrim = sid != null && String(sid).trim() !== "" ? String(sid).trim() : null;
  const bodyWithSession = sidTrim
    ? { ...body, sessionID: sidTrim }
    : _attachAgentSessionToBody(body);
  const axiosExtra = sidTrim
    ? {
        headers: {
          Authorization: `Bearer ${sidTrim}`,
          "X-Session-ID": sidTrim,
        },
      }
    : {};
  traceDashboardHeavyApiCall({
    endpoint: "/getLDSOrdersByAgentId",
    reason: `getLDSOrders dealType=${String(dealType)}`,
  });
  return performPostFetch(
    endpoint,
    bodyWithSession,
    "אירעה שגיאה בשליפת ההזמנות",
    axiosExtra
  );
};

/**
 * POST /api/lds/orders/merge-proposal — merge FlightsHotel orders into one proposal-ready row.
 */
const previewMergeLdsOrdersProposal = async (orderIds, options = {}) => {
  const endpoint = resolveApiUrl("/api/lds/orders/merge-proposal/preview");
  const body = {
    order_ids: orderIds,
    ...(Array.isArray(options.clientOrderTotals) && options.clientOrderTotals.length
      ? { client_order_totals: options.clientOrderTotals }
      : {}),
  };
  let sid = null;
  try {
    sid = await getSessionId();
  } catch {
    sid = null;
  }
  const sidTrim = sid != null && String(sid).trim() !== "" ? String(sid).trim() : null;
  const bodyWithSession = sidTrim ? { ...body, sessionID: sidTrim } : body;
  const axiosExtra = sidTrim
    ? {
        headers: {
          Authorization: `Bearer ${sidTrim}`,
          "X-Session-ID": sidTrim,
        },
      }
    : {};
  return performPostFetch(
    endpoint,
    bodyWithSession,
    "אירעה שגיאה בטעינת סיכום PROPOSAL מאוחד",
    axiosExtra
  );
};

const mergeLdsOrdersProposal = async (orderIds, options = {}) => {
  const endpoint = resolveApiUrl("/api/lds/orders/merge-proposal");
  const body = {
    order_ids: orderIds,
    mode: options.mode || "proposal",
    ...(Array.isArray(options.clientOrderTotals) && options.clientOrderTotals.length
      ? { client_order_totals: options.clientOrderTotals }
      : {}),
  };
  let sid = null;
  try {
    sid = await getSessionId();
  } catch {
    sid = null;
  }
  const sidTrim = sid != null && String(sid).trim() !== "" ? String(sid).trim() : null;
  const bodyWithSession = sidTrim ? { ...body, sessionID: sidTrim } : body;
  const axiosExtra = sidTrim
    ? {
        headers: {
          Authorization: `Bearer ${sidTrim}`,
          "X-Session-ID": sidTrim,
        },
      }
    : {};
  return performPostFetch(
    endpoint,
    bodyWithSession,
    "אירעה שגיאה ביצירת PROPOSAL מאוחד",
    axiosExtra
  );
};

const createFinance = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/createFinanceRequest`, data);
};

// -- Hotels - API Requests

// Get search hotel results
const searchHotels = async (data) => {
  const endpoint = `${ApiReqBaseUrl}/searchHotels`;
  traceDestinationSearch("[DESTINATION_PAYLOAD_TRACE]", {
    phase: "backend_request_call",
    supplierSearchEndpoint: "/searchHotels",
    destinationIdExists: Boolean(data?.hr_destination_id || data?.destinationId),
    destinationNameOnlySent: Boolean(
      !(data?.hr_destination_id || data?.destinationId) && data?.destinationName
    ),
    payloadDestinationEntity: summarizeDestinationEntity(data?.destinationEntity),
    payload: data,
  });
  return performPostFetch(endpoint, data);
};

/** Multi-room split search (V1); separate backend path from searchHotels. */
const searchHotelsComposite = async (data) => {
  const endpoint = `${ApiReqBaseUrl}/searchHotelsComposite`;
  traceDestinationSearch("[DESTINATION_PAYLOAD_TRACE]", {
    phase: "backend_request_call",
    supplierSearchEndpoint: "/searchHotelsComposite",
    destinationIdExists: Boolean(data?.hr_destination_id || data?.destinationId),
    destinationNameOnlySent: Boolean(
      !(data?.hr_destination_id || data?.destinationId) && data?.destinationName
    ),
    payloadDestinationEntity: summarizeDestinationEntity(data?.destinationEntity),
    payload: data,
  });
  return performPostFetch(endpoint, data);
};

const validateCompositeHotelQuote = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/validateCompositeHotelQuote`, data);
};

const searchFlightsInFlight = new Map();

const searchFlights = async (data) => {
  if (process.env.NODE_ENV === "development") {
    try {
      console.log(
        "[searchFlights] RAW inbound (pre-normalize):",
        JSON.stringify(data ?? null)
      );
    } catch {
      console.log("[searchFlights] RAW inbound (pre-normalize):", data);
    }
  }
  const payload = normalizeSearchFlightsPayloadForLds(data);
  const endpoint = `${ApiReqBaseUrl}/searchFlights`;
  const dpTrace = data?.__dpTrace;
  if (process.env.NODE_ENV === "development" && dpTrace) {
    const rooms = Array.isArray(payload.room_compositions)
      ? payload.room_compositions
      : [];
    const adults = rooms.reduce((sum, room) => sum + (Number(room?.adults) || 0), 0);
    const children = rooms.reduce((sum, room) => sum + (Number(room?.kids) || 0), 0);
    console.info("[DP_SEARCH_FLIGHTS_CALL_TRACE]", {
      caller: dpTrace.caller || "fhpService.searchFlights",
      reason: `${dpTrace.reason || "direct_searchFlights_call"}:service_send`,
      requestKey: dpTrace.requestKey || null,
      originCode: payload.origin || null,
      destinationCode: payload.destination || null,
      departureDate: payload.dep_date || null,
      returnDate: payload.ret_date || null,
      adults,
      children,
      roomsCount: rooms.length,
      timestamp: new Date().toISOString(),
    });
  }
  const requestKey = JSON.stringify(payload);
  const existingRequest = searchFlightsInFlight.get(requestKey);
  if (existingRequest) {
    if (process.env.NODE_ENV === "development") {
      console.info("[DP_SEARCH_EMPTY_REASON_TRACE]", {
        reason: "duplicate_searchFlights_payload_joined_in_flight_request",
        supplierResponseCode: null,
        validationErrors: [],
        missingChildAges: false,
        destinationCode: payload.destination || null,
        originCode: payload.origin || null,
      });
    }
    return existingRequest;
  }
  console.log("[searchFlights] selected tripType:", payload.dealType);
  console.log("[searchFlights] final search payload:", JSON.stringify(payload));
  console.log("[FLIGHT_SEARCH_DEBUG] endpoint", endpoint);
  console.log("[FLIGHT_SEARCH_DEBUG] payload", payload);
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const requestPromise = (async () => {
    try {
      const res = await performPostFetch(endpoint, payload);
      const status = res?.status ?? res?.response?.status ?? null;
      const body = res?.data;
      const deals = body?.deal;
      const resultsCount = Array.isArray(body)
        ? body.length
        : Array.isArray(deals)
          ? deals.length
          : deals
            ? 1
            : 0;
      if (process.env.NODE_ENV === "development") {
        const finishedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        console.info("[DP_SEARCH_API_RESPONSE_TRACE]", {
          endpoint: "/searchFlights",
          status,
          durationMs: Math.round(finishedAt - startedAt),
          hasResults: resultsCount > 0,
          resultsCount,
          errorCode: null,
          errorMessage: null,
        });
      }
      console.log("[FLIGHT_SEARCH_DEBUG] responseStatus", status);
      console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", resultsCount);
      console.log("[FLIGHT_SEARCH_DEBUG] error", null);
      return res;
    } catch (err) {
      const status = err?.response?.status ?? null;
      if (process.env.NODE_ENV === "development") {
        const finishedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        console.warn("[DP_SEARCH_API_RESPONSE_TRACE]", {
          endpoint: "/searchFlights",
          status,
          durationMs: Math.round(finishedAt - startedAt),
          hasResults: false,
          resultsCount: 0,
          errorCode: err?.response?.data?.error || err?.code || null,
          errorMessage:
            err?.response?.data?.message ||
            err?.response?.data?.errors?.[0]?.message_he ||
            err?.response?.data?.errors?.[0]?.message ||
            err?.message ||
            String(err),
        });
      }
      console.log("[FLIGHT_SEARCH_DEBUG] responseStatus", status);
      console.log("[FLIGHT_SEARCH_DEBUG] resultsCount", 0);
      console.log(
        "[FLIGHT_SEARCH_DEBUG] error",
        err?.response?.data?.message || err?.message || String(err)
      );
      throw err;
    }
  })();
  searchFlightsInFlight.set(requestKey, requestPromise);
  requestPromise.finally(() => {
    searchFlightsInFlight.delete(requestKey);
  });
  return requestPromise;
};

const searchLds = async (data, opts = {}) => {
  const axiosExtra = {};
  if (opts?.signal) {
    axiosExtra.signal = opts.signal;
  }
  let sid = null;
  try {
    sid = await getSessionId();
  } catch {
    sid = null;
  }
  const sidTrim =
    sid != null && String(sid).trim() !== ""
      ? String(sid).trim()
      : resolveAgentPostSessionIdSync() || null;
  const bodyWithSession = sidTrim
    ? {
        ...(data && typeof data === "object" && !Array.isArray(data) ? data : {}),
        sessionID: sidTrim,
      }
    : _attachAgentSessionToBody(data);
  const body = sanitizeLdsSearchRequestBody(
    stripPublicSiteTokenFromLdsInventorySearchBody(bodyWithSession)
  );
  if (sidTrim) {
    axiosExtra.headers = {
      Authorization: `Bearer ${sidTrim}`,
      "X-Session-ID": sidTrim,
    };
  }
  return performPostFetch(
    `${ApiReqBaseUrl}/searchLds`,
    body,
    undefined,
    axiosExtra
  );
};

/**
 * @deprecated Organized tour info must use `getTourInfo` from
 * `src/modules/organizedTours/api/tourInfoClient.js` (POST `/api/lds/organized-tour/info`).
 * This export remains for legacy callers only. Passing a string id still hits deprecated GET `/getOrgTourInfo/:id`.
 * No active Organized Tours UI should call this.
 */
const getOrgTourInfo = async (idOrPayload) => {
  if (idOrPayload && typeof idOrPayload === "object" && !Array.isArray(idOrPayload)) {
    const payload = {
      dealId: idOrPayload.dealId,
      tourName: idOrPayload.tourName,
      supplierId: idOrPayload.supplierId,
    };
    return getOrganizedTourInfoFromModule(payload);
  }
  const id = encodeURIComponent(String(idOrPayload ?? ""));
  const res = await performGetFetch(
    resolveApiUrl(`/getOrgTourInfo/${id}`),
    {},
    false
  );
  return res?.data ?? res;
};

const getActiveDestinations = async () => {
  return performGetFetch(`${ApiReqBaseUrl}/getActiveDestinations`);
};

const getLdsDates = async (data) => {
  return performPostFetch(
    `${ApiReqBaseUrl}/getLdsDates`,
    _attachAgentSessionToBody(data)
  );
};

const searchHotelsBasis = async (data) => {
  // Create API request for hotel search
  try {
    return await axios.post(`${ApiReqBaseUrl}/searchHotelsBasis`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Get hotel profile data (optional { signal } for AbortController)
const getHotelsProfileData = async (data, opts = {}) => {
  const cacheKey = buildHotelProfileDataCacheKey(data);
  const now = Date.now();
  const cached = hotelProfileDataRequestCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    if (typeof window !== "undefined") {
      window.__HR_HOTEL_PROFILE_DATA_CACHE_HITS__ =
        (window.__HR_HOTEL_PROFILE_DATA_CACHE_HITS__ || 0) + 1;
    }
    return cached.promise;
  }

  const axiosExtra = opts.signal ? { signal: opts.signal } : {};
  const promise = performPostFetch(`${ApiReqBaseUrl}/hotelInformation`, data, undefined, axiosExtra)
    .then((res) => {
      hotelProfileDataRequestCache.set(cacheKey, {
        promise: Promise.resolve(res),
        expiresAt: Date.now() + HOTEL_PROFILE_DATA_CACHE_TTL_MS,
      });
      return res;
    })
    .catch((err) => {
      hotelProfileDataRequestCache.delete(cacheKey);
      throw err;
    });
  hotelProfileDataRequestCache.set(cacheKey, {
    promise,
    expiresAt: now + HOTEL_PROFILE_DATA_CACHE_TTL_MS,
  });
  return promise;
};

// Get hotel image
const getHotelsImage = async (data) => {
  // Create API request for hotel profile main image
  try {
    return await axios.post(`${ApiReqBaseUrl}/getImage`, data);
  } catch (err) {
    console.log({ err });
  }
};

const getHotelsProfileImages = async (data) => {
  // Create API request for hotels profileimages
  try {
    return await axios.post(`${ApiReqBaseUrl}/hotelImages`, data);
  } catch (err) {
    console.log({ err });
  }
};

const getHotelsDescription = async (data) => {
  // Create API request for hotel's description
  try {
    return await axios.post(`${ApiReqBaseUrl}/hotelDescriptions`, data);
  } catch (err) {
    console.log({ err });
  }
};

const bookHotel = async (data) => {
  // Create API request for booking
  try {
    const res = await axios.post(`${ApiReqBaseUrl}/bookHotel`, data);
    return res;
  } catch (err) {
    console.log({ err });
    if (err.response && err.response.status === 400) {
      return err.response;
    }
    throw err;
  }
};

/** Multi-room composite: N supplier books → one HR order (backend flag ENABLE_COMPOSITE_BOOKING_ORCHESTRATION). */
const bookCompositeHotel = async (data) => {
  try {
    const res = await axios.post(`${ApiReqBaseUrl}/bookCompositeHotel`, data);
    return res;
  } catch (err) {
    console.log({ err });
    if (err.response && (err.response.status === 400 || err.response.status === 403)) {
      return err.response;
    }
    throw err;
  }
};

const newHotelOrder = async (data) => {
  try {
    const debugPayloadKeys =
      data && typeof data === "object" ? Object.keys(data) : [];
    const legacyPayloadShape =
      debugPayloadKeys.length === 4 &&
      debugPayloadKeys.includes("offer") &&
      debugPayloadKeys.includes("search_phrase") &&
      debugPayloadKeys.includes("packagePriceDetails") &&
      debugPayloadKeys.includes("commisions");
    if (legacyPayloadShape) {
      return {
        data: {
          status: "fail",
          code: "FE_BLOCKED_LEGACY_HOTEL_ORDER_PAYLOAD",
          message: "שגיאה בבניית הזמנה — חסרים פרטי הזמנה/נוסע",
          missingFields: [
            "session",
            "checkIn",
            "checkOut",
            "package",
            "agentID",
            "hotel",
            "totalPrice",
            "fullname",
            "email",
            "cell",
            "passengers",
          ],
          invalidFields: ["legacy_payload_shape"],
          payloadKeys: debugPayloadKeys,
        },
        status: 0,
      };
    }
    const requiredFields = [
      "session",
      "checkIn",
      "checkOut",
      "package",
      "agentID",
      "hotel",
      "totalPrice",
      "offer",
      "fullname",
      "email",
      "cell",
      "passengers",
    ];
    const isMissingHotelOrderField = (value) => {
      if (value == null) return true;
      if (typeof value === "string" && value.trim() === "") return true;
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "object") return Object.keys(value).length === 0;
      return false;
    };
    const payloadKeys = debugPayloadKeys;
    const missingFields = requiredFields.filter((field) =>
      isMissingHotelOrderField(data?.[field])
    );
    const invalidFields = [];
    if (!Array.isArray(data?.passengers) || data.passengers.length === 0) {
      invalidFields.push("passengers");
    }
    if (
      typeof data?.totalPrice === "object" ||
      !Number.isFinite(Number(data?.totalPrice)) ||
      Number(data?.totalPrice) <= 0
    ) {
      invalidFields.push("totalPrice");
    }
    if (missingFields.length > 0 || invalidFields.length > 0) {
      const audit = {
        payloadKeys,
        missingFields,
        invalidFields,
        totalPrice: data?.totalPrice,
        session: data?.session ?? data?.sessionID ?? data?.SessionID ?? null,
        checkIn: data?.checkIn ?? null,
        checkOut: data?.checkOut ?? null,
        packageId:
          data?.package?.PackageId ??
          data?.package?.packageID ??
          data?.package?.packageId ??
          null,
        hotelId:
          data?.hotel?.HotelId ??
          data?.hotel?.hotelId ??
          data?.hotel?.Result?.[0]?.HotelId ??
          null,
        agentID: data?.agentID ?? data?.agent_id ?? null,
        leadPassengerSummary: Array.isArray(data?.passengers)
          ? {
              passengersCount: data.passengers.length,
              firstPassengerKeys:
                data.passengers[0] && typeof data.passengers[0] === "object"
                  ? Object.keys(data.passengers[0])
                  : [],
            }
          : null,
      };
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[HOTEL_CREATE_OFFER_PAYLOAD_AUDIT]", audit);
        // eslint-disable-next-line no-console
        console.error("[HOTEL_NEW_ORDER_BLOCKED_PARTIAL_PAYLOAD]", audit);
      }
      return {
        data: {
          status: "fail",
          code: "FE_BLOCKED_PARTIAL_HOTEL_ORDER_PAYLOAD",
          message: [
            missingFields.length
              ? `חסרים שדות חובה: ${missingFields.join(", ")}`
              : "",
            invalidFields.length
              ? `שדות לא תקינים: ${invalidFields.join(", ")}`
              : "",
          ]
            .filter(Boolean)
            .join(". "),
          missingFields,
          invalidFields,
          payloadKeys,
        },
        status: 0,
      };
    }
    const quoteLinkMode =
      data?.validationMode === "quote_link" ||
      data?.validationMode === "quote_proposal" ||
      data?.isOffer === true;
    if (import.meta.env?.DEV) {
      if (!quoteLinkMode) {
        // eslint-disable-next-line no-console
        console.info("[HOTEL_NEW_ORDER_REQUEST]", {
          validationMode: data?.validationMode ?? "booking_payment",
          isOffer: Boolean(data?.isOffer),
          payloadKeys: data && typeof data === "object" ? Object.keys(data) : [],
        });
      }
    }
    if (import.meta.env?.DEV && quoteLinkMode) {
      // eslint-disable-next-line no-console
      console.info("[HOTEL_QUOTE_LINK_MODE_TRACE]", {
        endpointUsed: "/newHotelOrder",
        validationMode: data?.validationMode ?? (data?.isOffer ? "quote_link" : null),
        requiredFields: [
          "session",
          "hotel",
          "package",
          "checkIn",
          "checkOut",
          "occupancySummary",
          "totalPrice",
          "pricingSnapshot",
          "agentID",
        ],
        skippedPassengerValidation: true,
        payloadKeys: data && typeof data === "object" ? Object.keys(data) : [],
      });
    }
    // #region agent log
    fetch("http://127.0.0.1:7890/ingest/b0b67dfb-7306-4d13-82f5-46e25cc15ee5", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "e5c5ca",
      },
      body: JSON.stringify({
        sessionId: "e5c5ca",
        runId: "pre-fix-legacy-path",
        hypothesisId: "H3,H4",
        location: "src/services/fhpService.js:newHotelOrder:beforeAxios",
        message: "newHotelOrder about to POST to BE",
        data: {
          payloadKeys,
          hasSession: Boolean(data?.session),
          hasPackage: Boolean(data?.package),
          hasHotel: Boolean(data?.hotel),
          hasOffer: Boolean(data?.offer),
          passengersLength: Array.isArray(data?.passengers)
            ? data.passengers.length
            : null,
          totalPriceType: typeof data?.totalPrice,
          totalPriceIsFinite: Number.isFinite(Number(data?.totalPrice)),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    const res = await axios.post(`${ApiReqBaseUrl}/newHotelOrder`, data);
    return res;
  } catch (err) {
    if (err?.response?.data != null) {
      return { data: err.response.data, status: err.response.status };
    }
    throw err;
  }
};

export const saveDynamicPackage = async (data) => {
  // Create API request for new hotel order
  try {
    return await axios.post(`${ApiReqBaseUrl}/saveDynamicPackage`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Get delayed-pay order (Gimmonix) — session SSOT like getOrderById (Admin never from client isAdmin alone).
const getDelayedPayOrder = async (data) => {
  try {
    const sessionRaw = await getSessionId();
    const sessionID =
      sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
        ? sessionRaw
        : _resolveWalletSessionIdSync() || null;
    const body = {
      ...(data && typeof data === "object" ? data : {}),
    };
    if (sessionID) body.sessionID = sessionID;
    return await axios.post(`${ApiReqBaseUrl}/gimmonix/orderByID`, body, {
      headers: sessionID
        ? { "X-Session-ID": String(sessionID), "X-Session-Id": String(sessionID) }
        : undefined,
    });
  } catch (err) {
    console.error("[getDelayedPayOrder]", err);
    // Prefer HTTP body (perms/fail) over swallowing — PaymentAgent maps status.
    if (err?.response) return err.response;
    throw err;
  }
};

// Get order by specific ID (admin / finance — Gimmonix hotel order)
// Auth: sessionID only — BE derives Admin/Agent from activeSessions (never trust client isAdmin).
export const getOrderById = async (orderId) => {
  try {
    console.log(`🔍 Fetching order with ID: ${orderId}`);
    const sessionRaw = await getSessionId();
    const sessionID =
      sessionRaw && typeof sessionRaw === "string" && sessionRaw !== "false"
        ? sessionRaw
        : _resolveWalletSessionIdSync() || null;
    const body = {
      orderId,
      idSource: "orders.id",
      bookingType: "hotel",
    };
    if (sessionID) body.sessionID = sessionID;
    // Agent scope hint only — ownership is still enforced on BE from session.
    try {
      const u = getUserInfo();
      const aid = u?.id ?? u?.agentId ?? u?.agent_id;
      if (aid != null && String(aid).trim() !== "") {
        body.agentId = String(aid).trim();
      }
    } catch {
      /* ignore */
    }
    const response = await axios.post(`${ApiReqBaseUrl}/gimmonix/orderByID`, body, {
      headers: sessionID
        ? { "X-Session-ID": String(sessionID), "X-Session-Id": String(sessionID) }
        : undefined,
    });
    // Keep fail/perms payload so HotelOrderDetailsPage can show Unauthorized vs not-found.
    console.log(`✅ Order data received:`, response.data);
    return response;
  } catch (err) {
    console.error(`❌ Error fetching order ${orderId}:`, err);
    throw err;
  }
};

const updateOrderFinancials = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/updateOrderFinancials`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Delayed payment
const delayedPayment = async (data) => {
  // Create API request for delayed payment
  try {
    return await axios.post(`${ApiReqBaseUrl}/delayedPayment`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Check In Info
const checkInInfo = async (data) => {
  // Create API request for check in info
  try {
    return await axios.post(`${ApiReqBaseUrl}/checkInInfo`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Get cancellation Policies — POST full body (parity with origin/main); do not strip fields here.
const cancellationPolicies = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/cancellationPolicies`, data);
  } catch (err) {
    console.log({ err });
  }
};

const validateHotelPackageBeforeContinue = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/validateHotelPackageBeforeContinue`, data);
  } catch (err) {
    console.log({ err });
    throw err;
  }
};

const dynamicPackageRoomsPrecheck = async (data) => {
  return performPostFetch(resolveApiUrl("/api/dynamic-package/rooms/precheck"), data);
};

// Get Facilities of hotel
const getFacilities = async (data) => {
  // Create API request for getFacilities
  try {
    return await axios.post(`${ApiReqBaseUrl}/getFacilities`, data);
  } catch (err) {
    console.log({ err });
  }
};

// Pay
const pay = async (data) => {
  // Pay
  try {
    return await axios.post(`${ApiReqBaseUrl}/pay`, data);
  } catch (err) {
    console.log({ err });
  }
};

const sendSuccessOrder = async (base64Image, mail) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/mailOrder`, {
      mail,
      base64Image,
    });
  } catch (err) {
    console.log({ err });
  }
};

export const ensureGimmonixConfirmationEmail = async (orderId) => {
  try {
    return await axios.post(
      `${ApiReqBaseUrl}/orders/${encodeURIComponent(orderId)}/ensure-confirmation-email`,
      {}
    );
  } catch (err) {
    console.log({ err });
    return { data: { status: "error", error: err?.message } };
  }
};

const getAgents = async () => {
  return performGetFetch(`${ApiReqBaseUrl}/getAgents`);
};

/** Mini-site: POST /getAgentById { agentId } → { success, data }; falls back to scanning /getAgents. */
const getAgentById = async (agentId) => {
  if (agentId == null || agentId === "") {
    return { data: { success: false } };
  }
  try {
    const r = await axios.post(`${ApiReqBaseUrl}/getAgentById`, {
      agentId: String(agentId),
    });
    if (r?.data?.success && r?.data?.data) return r;
  } catch (_) {
    /* try fallback */
  }
  try {
    const r = await getAgents();
    const list = Array.isArray(r?.data) ? r.data : r?.data?.agents || [];
    const agent = list.find(
      (a) =>
        String(a?.id ?? a?.agent_id ?? a?.agentId) === String(agentId)
    );
    if (agent) {
      return { data: { success: true, data: agent } };
    }
  } catch (_) {
    /* ignore */
  }
  return { data: { success: false } };
};

/**
 * מנרמל תגובת /getFeaturedPackagesSettings — השרת מחזיר { status, data: { packageType, destinations, numberOfPackages } }
 * בעוד axios שרוי ב־response.data, כלומר ההגדרות האמיתיות ב־response.data.data.
 */
function normalizeFeaturedPackagesSettingsResponse(axiosResponse) {
  const root = axiosResponse?.data;
  if (!root || typeof root !== "object") return null;
  const inner = root.data;
  if (
    inner &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    ("destinations" in inner || "packageType" in inner || "numberOfPackages" in inner)
  ) {
    return inner;
  }
  if ("destinations" in root || "packageType" in root || "numberOfPackages" in root) {
    return root;
  }
  return null;
}

const getFeaturedPackagesSettings = async (agentId) => {
  try {
    return await axios.get(`${ApiReqBaseUrl}/getFeaturedPackagesSettings`, {
      params: { agentId: agentId != null ? String(agentId) : undefined },
    });
  } catch (err) {
    return err.response ?? { data: { packages: [] } };
  }
};

const getAllAgentsWithFinancialData = async () => {
  try {
    // Bulk wallet SSOT can take tens of seconds for all agents.
    return await axios.get(`${ApiReqBaseUrl}/getAllAgentsWithFinancialData`, {
      withCredentials: true,
      timeout: 120000,
    });
  } catch (err) {
    return { data: null };
  }
};

/**
 * Silent admin wallet reset — hide available or future lines without agent email.
 * Allowed only for adir@hotelsriver.com on the server.
 */
export async function postAdminSilentWalletReset(agentId, bucket) {
  const id = Number(agentId);
  const b = String(bucket || "").trim().toLowerCase();
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("agent_id_invalid");
  }
  if (b !== "available" && b !== "future") {
    throw new Error("bucket_invalid");
  }
  const path =
    b === "available"
      ? "/api/admin/wallet/silent-reset-available"
      : "/api/admin/wallet/silent-reset-future";
  const url = resolveApiUrl(path);
  const sid =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("sessionID") || ""
      : "";
  const { data } = await axios.post(
    url,
    { agent_id: id, sessionID: sid },
    {
      headers: adminBillingRequestHeaders(),
      withCredentials: true,
      timeout: 120000,
    }
  );
  return data;
}

/** List silent resets that can still be restored (adir-only). */
export async function fetchAdminSilentResetRestorable(agentId) {
  const url = resolveApiUrl("/api/admin/wallet/silent-reset-restorable");
  const params = {};
  if (agentId != null && Number.isFinite(Number(agentId)) && Number(agentId) > 0) {
    params.agent_id = Number(agentId);
  }
  const { data } = await axios.get(url, {
    params,
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
    timeout: 60000,
  });
  return data;
}

/** Restore a silent reset by finance req number — no agent email. */
export async function postAdminSilentWalletRestore(financeReqNumber) {
  const req = Number(financeReqNumber);
  if (!Number.isFinite(req) || req <= 0) {
    throw new Error("finance_req_number_invalid");
  }
  const url = resolveApiUrl("/api/admin/wallet/silent-reset-restore");
  const sid =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("sessionID") || ""
      : "";
  const { data } = await axios.post(
    url,
    { finance_req_number: req, sessionID: sid },
    {
      headers: adminBillingRequestHeaders(),
      withCredentials: true,
      timeout: 120000,
    }
  );
  return data;
}

/** כותרות ל־/api/admin/agents/* — Bearer (סשן אדמין) ואופציונלי VITE_ADMIN_API_KEY */
function adminBillingRequestHeaders() {
  const h = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  try {
    if (typeof localStorage !== "undefined") {
      const sid = localStorage.getItem("sessionID");
      if (sid) h.Authorization = `Bearer ${sid}`;
    }
  } catch (_) {
    /* ignore */
  }
  const k = import.meta.env?.VITE_ADMIN_API_KEY;
  if (k != null && String(k).trim() !== "") {
    h["X-Admin-Api-Key"] = String(k).trim();
  }
  return h;
}

/**
 * GET /api/admin/agents/billing-summary — מונים גלובליים (ללא שליפת כל הסוכנים).
 */
export async function fetchAdminAgentsBillingSummary() {
  const url = resolveApiUrl("/api/admin/agents/billing-summary");
  const { data } = await axios.get(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

/**
 * GET /api/admin/agents/package-filter-options — רשימת חבילות ל-dropdown (id:/label).
 */
export async function fetchAdminPackageFilterOptions() {
  const url = resolveApiUrl("/api/admin/agents/package-filter-options");
  const { data } = await axios.get(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

/**
 * GET /api/admin/agents/billing-overview
 * @param {object} [params]
 * @param {string} [params.search]
 * @param {string} [params.registrationStatus]
 * @param {string} [params.subscriptionStatus]
 * @param {string} [params.status] חשבון: active | inactive (agents.activated)
 * @param {number} [params.page]
 * @param {number} [params.page_size]
 * @param {number} [params.limit]
 * @param {number} [params.offset]
 * @param {string} [params.sort]
 * @param {string} [params.sort_by]
 * @param {string} [params.sortDir]
 * @param {string} [params.sort_dir]
 * @param {boolean} [params.hasPaymentMethod]
 * @param {boolean} [params.management] true = העשרת עמלה/ארנק לעמוד ניהול סוכנים (ברירת מחדל true)
 */
export async function fetchAgentsBillingOverview(params = {}) {
  const url = resolveApiUrl("/api/admin/agents/billing-overview");
  let hasPm;
  if (params.hasPaymentMethod === true) hasPm = "1";
  else if (params.hasPaymentMethod === false) hasPm = "0";
  else hasPm = undefined;
  const usePage = params.page != null && params.page !== "";
  const managementDefault = params.management !== false;
  const tri = (v) => {
    if (v === undefined || v === null) return undefined;
    if (v === true || v === "true" || v === "1") return "1";
    if (v === false || v === "false" || v === "0") return "0";
    return undefined;
  };

  const q = {
    search: params.search || undefined,
    registrationStatus: params.registrationStatus || undefined,
    subscriptionStatus: params.subscriptionStatus || undefined,
    subscriptionEmpty: params.subscriptionEmpty === true ? "1" : undefined,
    status: params.status || undefined,
    managementStatus: params.managementStatus || params.management_status || undefined,
    package: params.package || params.packageFilter || undefined,
    customCommissionOverride: tri(params.customCommissionOverride),
    dynamicPackages: tri(params.dynamicPackages),
    searchFlights: tri(params.searchFlights),
    hasPaymentMethod: hasPm,
    sort: params.sort || params.sort_by || undefined,
    sort_by: params.sort_by || params.sort || undefined,
    sortDir: params.sortDir || params.sort_dir || undefined,
    sort_dir: params.sort_dir || params.sortDir || undefined,
    management: managementDefault ? "1" : "0",
  };
  if (usePage) {
    q.page = params.page;
    q.page_size = params.page_size ?? 25;
  } else {
    q.limit = params.limit ?? 25;
    q.offset = params.offset ?? 0;
  }
  const { data } = await axios.get(url, {
    params: q,
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function postAdminBillingApproveAgent(agentId, approvedByUserId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/approve`);
  const body = {};
  if (approvedByUserId != null && approvedByUserId !== "") {
    body.approvedByUserId = Number(approvedByUserId);
  }
  const { data } = await axios.post(url, body, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function postAdminBillingDeactivateSubscription(agentId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/deactivate-subscription`);
  const { data } = await axios.post(
    url,
    {},
    {
      headers: adminBillingRequestHeaders(),
      withCredentials: true,
    }
  );
  return data;
}

export async function postAdminBillingReactivateSubscription(agentId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/reactivate-subscription`);
  const { data } = await axios.post(
    url,
    {},
    {
      headers: adminBillingRequestHeaders(),
      withCredentials: true,
    }
  );
  return data;
}

/**
 * POST /api/admin/agents/:id/delete — hard-delete inactive / paused agent from DB.
 */
export async function deleteAdminInactiveAgent(agentId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/delete`);
  const { data } = await axios.post(
    url,
    {},
    {
      headers: adminBillingRequestHeaders(),
      withCredentials: true,
    }
  );
  return data;
}

/**
 * Admin subscription management (upgrade / downgrade / payment link / history).
 * Backend routes registered in register_agent_billing_routes.py:
 *   GET  /api/admin/agents/:id/subscription
 *   POST /api/admin/agents/:id/subscription/change
 *   POST /api/admin/agents/:id/subscription/payment-link
 *   GET  /api/admin/agents/:id/subscription/history
 */
export async function getAgentSubscriptionSnapshot(agentId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/subscription`);
  const { data } = await axios.get(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function changeAgentSubscription(
  agentId,
  { targetPackageCode, changeMode = "immediate", notes = "" } = {}
) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/subscription/change`);
  const body = {
    target_package_code: targetPackageCode,
    change_mode: changeMode,
  };
  if (notes) body.notes = notes;
  const { data } = await axios.post(url, body, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function createAgentSubscriptionPaymentLink(
  agentId,
  { targetPackageCode, notes = "" } = {}
) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/subscription/payment-link`);
  const body = { target_package_code: targetPackageCode };
  if (notes) body.notes = notes;
  const { data } = await axios.post(url, body, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function getAgentSubscriptionHistory(agentId) {
  const id = Number(agentId);
  const url = resolveApiUrl(`/api/admin/agents/${id}/subscription/history`);
  const { data } = await axios.get(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function getAdminAcademyVideos() {
  const url = resolveApiUrl("/api/academy/videos");
  const { data } = await axios.get(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function createAdminAcademyVideo(payload) {
  const url = resolveApiUrl("/api/admin/academy/videos");
  const { data } = await axios.post(url, payload, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function updateAdminAcademyVideo(videoId, payload) {
  const url = resolveApiUrl(`/api/admin/academy/videos/${encodeURIComponent(videoId)}`);
  const { data } = await axios.patch(url, payload, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

export async function deleteAdminAcademyVideo(videoId) {
  const url = resolveApiUrl(`/api/admin/academy/videos/${encodeURIComponent(videoId)}`);
  const { data } = await axios.delete(url, {
    headers: adminBillingRequestHeaders(),
    withCredentials: true,
  });
  return data;
}

const activateAgent = async (agentId) => {
  try {
    return await performPostFetch(`${ApiReqBaseUrl}/activateAgent`, {
      agentId,
    });
  } catch (error) {
    console.error("Error activating agent:", error);
    throw error;
  }
};

const getAgentRecommendedByCount = async (id, image) => {
  return performGetFetch(`${ApiReqBaseUrl}/getAgentRecommendedByCount`);
};

const getHotelInfo = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/getHotelInfo`, data);
};

const updateLdsHandlingfee = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/setLdsHandlingfee`, data);
};

const updateCurrency = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/updateCurrency`, data);
};

/** ניתוב אוטומטי LDS / Gimmonix לפי id (אותו כמו PaymentAgent orderByID) */
const fetchUnifiedOrderById = async (payload) =>
  performPostFetch(`${ApiReqBaseUrl}/orderByID`, payload);

// LDS row: unified POST /orderByID — same server routing as /lds/orderByID (LDS + proposal fallback).
// Dev note: Vite proxy keeps /api/lds/* as-is → Flask has no /api/lds/orderByID → was 404. /api/orderByID strips to /orderByID.
const getLdsOrder = async (data) => {
  const res = await performPostFetch(
    `${ApiReqBaseUrl}/orderByID`,
    _attachAgentSessionToBody(data)
  );
  if (res?.data && typeof res.data === "object" && res.data.status === "fail") {
    const ldsOrderFetchError = { ...res.data };
    return { ...res, data: null, ldsOrderFetchError };
  }
  return res;
};

const getLDSOrderById = async (data) => {
  return performPostFetch(
    `${ApiReqBaseUrl}/findLDSById`,
    _attachAgentSessionToBody(data)
  );
};

const createLdsNewOrder = async (data) => {
  const payload =
    data != null && typeof data === "object" && !Array.isArray(data)
      ? { ...data }
      : data;
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    (payload.agent_id == null || payload.agent_id === "")
  ) {
    if (payload.agentid != null && payload.agentid !== "") {
      payload.agent_id = payload.agentid;
    } else if (payload.agentID != null && payload.agentID !== "") {
      payload.agent_id = payload.agentID;
    } else {
      const user = getUserDetails();
      if (user?.id != null && user.id !== "") {
        payload.agent_id = user.id;
      }
    }
    if (
      (payload.agent_id == null || payload.agent_id === "") &&
      typeof window !== "undefined" &&
      window.__HR_PUBLIC_SITE_AGENT_ID != null &&
      String(window.__HR_PUBLIC_SITE_AGENT_ID).trim() !== ""
    ) {
      payload.agent_id = String(window.__HR_PUBLIC_SITE_AGENT_ID).trim();
    }
    if (
      (payload.agent_id == null || payload.agent_id === "") &&
      typeof window !== "undefined"
    ) {
      const qsSiteId = new URLSearchParams(window.location.search).get("siteId");
      if (qsSiteId && String(qsSiteId).trim() !== "" && !payload.agent_public_site_id) {
        payload.agent_public_site_id = String(qsSiteId).trim();
      }
    }
  }
  return performPostFetch(
    `${ApiReqBaseUrl}/createLdsNewOrder`,
    _attachAgentSessionToBody(payload)
  );
};

const updateLdsOrder = async (data) => {
  const payload =
    data != null && typeof data === "object" && !Array.isArray(data)
      ? { ...data }
      : data;
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    payload.is_public_site_order === true &&
    !payload.public_site_id &&
    !payload.agent_public_site_id &&
    typeof window !== "undefined"
  ) {
    const qsSiteId = new URLSearchParams(window.location.search).get("siteId");
    if (qsSiteId && String(qsSiteId).trim() !== "") {
      payload.public_site_id = String(qsSiteId).trim();
      payload.agent_public_site_id = String(qsSiteId).trim();
    }
  }
  return performPostFetch(
    `${ApiReqBaseUrl}/updateLdsOrder`,
    _attachAgentSessionToBody(payload)
  );
};

const ensureLdsBankTransferConfirmation = async (orderId, extraBody) => {
  const id = orderId == null ? "" : String(orderId).trim();
  if (!id) return null;
  const extra =
    extraBody && typeof extraBody === "object" && !Array.isArray(extraBody) ? extraBody : {};
  return performPostFetch(
    `${ApiReqBaseUrl}/lds_orders/${encodeURIComponent(id)}/ensure-bank-transfer-confirmation`,
    _attachAgentSessionToBody({ ...extra }),
    false
  );
};

const transferOrderAgent = async ({ productType, orderId, targetAgentId }) => {
  let sid = null;
  try {
    sid = await getSessionId();
  } catch {
    sid = null;
  }
  const body = {
    productType,
    orderId,
    targetAgentId,
    ...(sid != null && String(sid).trim() !== "" ? { sessionID: String(sid).trim() } : {}),
  };
  try {
    return await axios.post(`${ApiReqBaseUrl}/api/orders/transfer-agent`, body, {
      withCredentials: true,
    });
  } catch (err) {
    return err.response ?? null;
  }
};

const findExtendedDeals = async (data) => {
  const body =
    data && typeof data === "object" && !Array.isArray(data) ? { ...data } : {};
  if (typeof window !== "undefined") {
    const siteId = new URLSearchParams(window.location.search || "").get("siteId");
    if (siteId && !body.siteId && !body.site_id && !body.public_site_id) {
      body.siteId = siteId;
    }
  }
  return performPostFetch(
    `${ApiReqBaseUrl}/findExtendedDeals`,
    _attachAgentSessionToBody(body)
  );
};

/** Phase 1: backend canonical LDS/Vacation package pricing for Packages profile display.
 * @param {object} data
 * @param {import("axios").AxiosRequestConfig} [axiosExtra] e.g. `{ signal: AbortSignal }`
 */
const fetchVpCanonicalPricing = async (data, axiosExtra = {}) =>
  performPostFetch(
    `${ApiReqBaseUrl}/vpCanonicalPricing`,
    _attachAgentSessionToBody(data),
    undefined,
    axiosExtra
  );

const getPackageFlightLinks = async (data) =>
  performPostFetch(
    `${ApiReqBaseUrl}/getPackageFlightLinks`,
    _attachAgentSessionToBody(data)
  );

const payLds = async (data) =>
  performPostFetchThrowError(`${ApiReqBaseUrl}/payLds`, data);

/** Minimal fetch Response shape for callers that use res.ok / res.json() (goes through axios in-flight dedup). */
function axiosToFetchLikeResponse(axRes) {
  const data = axRes?.data;
  const status = axRes?.status ?? 0;
  const ok = status >= 200 && status < 300;
  return {
    ok,
    status,
    statusText: axRes?.statusText || "",
    json: async () => data,
    text: async () =>
      typeof data === "string" ? data : JSON.stringify(data ?? ""),
  };
}

const createLdsPaymentLink = async (currentCard, orderId) => {
  const body = {
    amount:
      currentCard.numberofpayments && currentCard.numberofpayments > 1
        ? currentCard.amountWithInterest
        : currentCard.amount,
    actualAmount: currentCard.amount,
    currency: "ILS",
    orderId: orderId,
    tash: currentCard.numberofpayments,
  };
  try {
    const { peekChatPaymentLockForApi } = await import(
      "../stores/customerChatPaymentStore"
    );
    const lock = peekChatPaymentLockForApi();
    if (lock && lock.paymentPriceIls != null && Number.isFinite(Number(lock.paymentPriceIls))) {
      body.chat_lock_price_ils = Math.round(Number(lock.paymentPriceIls));
    }
  } catch {
    /* ignore */
  }
  const axRes = await axios.post(ApiReqBaseUrl + "/getLDSPaymentUrl", body, {
    headers: { "Content-Type": "application/json" },
    validateStatus: () => true,
  });
  return axiosToFetchLikeResponse(axRes);
};

const createLdsOrderDetailsPaymentLink = async ({
  orderId,
  amount,
  tash = 1,
  source = "order_details_partial_payment",
  paymentNote = null,
}) => {
  const sidRaw = await getSessionId().catch(() => null);
  const sid =
    sidRaw != null && String(sidRaw).trim() !== ""
      ? String(sidRaw).trim()
      : "";
  const body = {
    amount,
    tash,
    source,
    ...(paymentNote ? { payment_note: paymentNote, payment_reason: paymentNote } : {}),
    ...(sid ? { sessionID: sid } : {}),
  };
  const headers = {
    "Content-Type": "application/json",
    ...(sid
      ? {
          Authorization: `Bearer ${sid}`,
          "X-Session-ID": sid,
        }
      : {}),
  };
  return axios.post(
    `${ApiReqBaseUrl}/api/lds-orders/${encodeURIComponent(String(orderId ?? "").trim())}/payment-link`,
    body,
    {
      headers,
      withCredentials: true,
      validateStatus: () => true,
    }
  );
};

const fetchPackageOrderPaymentLinks = async (orderId) => {
  const sidRaw = await getSessionId().catch(() => null);
  const sid =
    sidRaw != null && String(sidRaw).trim() !== ""
      ? String(sidRaw).trim()
      : "";
  const headers = {
    Accept: "application/json",
    ...(sid
      ? {
          Authorization: `Bearer ${sid}`,
          "X-Session-ID": sid,
        }
      : {}),
  };
  return axios.get(
    `${ApiReqBaseUrl}/api/orders/packages/${encodeURIComponent(String(orderId ?? "").trim())}/payment-links`,
    {
      headers,
      withCredentials: true,
      validateStatus: () => true,
    }
  );
};


/**
 * Delete an unpaid package (LDS) payment link.
 * Uses existing /removeLDSPaymentUrl (expects payment URL, not session id).
 * @param {string} orderId
 * @param {string} linkIdOrUrl payment URL preferred; id fallback only if it is a URL
 */
const deletePackageOrderPaymentLink = async (orderId, linkIdOrUrl) => {
  const paymentLink = String(linkIdOrUrl ?? "").trim();
  const oid = String(orderId ?? "").trim();
  const axRes = await axios.post(
    ApiReqBaseUrl + "/removeLDSPaymentUrl",
    {
      paymentLink,
      orderId: oid,
    },
    {
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      withCredentials: true,
      validateStatus: () => true,
    }
  );
  // Normalize to { ok, [order] } so PackageOrderPaymentLinksList can treat success uniformly.
  const raw = axRes?.data;
  if (axRes.status >= 200 && axRes.status < 300 && raw && typeof raw === "object") {
    if (raw.ok === false || raw.error || raw.status === "fail") {
      return {
        ...axRes,
        data: {
          ok: false,
          error: raw.error || raw.message || "מחיקת הלינק נכשלה",
          message: raw.message || raw.error,
        },
      };
    }
    return {
      ...axRes,
      data: {
        ok: true,
        order: raw.order || raw,
      },
    };
  }
  return axRes;
};

const ADMIN_ORDER_COMMISSION_TYPE_SLUG = {
  package: "packages",
  flight: "flights",
  "organized-tour": "organized-tours",
  "dynamic-package": "dynamic",
  hotel: "hotel",
  dynamic: "dynamic",
  cruise: "cruise",
};

export function resolveAdminOrderCommissionApiType(orderType) {
  const key = String(orderType || "package").trim().toLowerCase();
  return ADMIN_ORDER_COMMISSION_TYPE_SLUG[key] || key;
}

/** Admin: PATCH agent commission on order row (wallet SSOT follows DB). */
const patchAdminOrderCommission = async ({
  orderType,
  orderId,
  commission_amount_ils,
  reason,
  allow_zero = false,
}) => {
  const apiType = resolveAdminOrderCommissionApiType(orderType);
  const oid = encodeURIComponent(String(orderId ?? "").trim());
  const body = _attachAgentSessionToBody({
    commission_amount_ils,
    reason,
    ...(allow_zero ? { allow_zero: true } : {}),
  });
  const res = await axios.patch(
    resolveApiUrl(`/api/admin/orders/${apiType}/${oid}/commission`),
    body,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ..._walletBearerHeaders(),
      },
      withCredentials: true,
      validateStatus: () => true,
    }
  );
  if (res.status === 401 || res.status === 403) {
    _reactToWalletIdentityHttpError({ response: res });
  }
  return res;
};

/** Dynamic package / flights_new agent — POST /getDynamicPaymentUrl (multi-card split). */
const createDynamicPaymentLink = async (currentCard, orderId, options = {}) => {
  const cardIdx = options.cardIndex;
  const body = {
    amount:
      currentCard.numberofpayments && currentCard.numberofpayments > 1
        ? currentCard.amountWithInterest
        : currentCard.amount,
    actualAmount: currentCard.amount,
    currency: "ILS",
    orderId,
    tash: currentCard.numberofpayments,
    ...(cardIdx != null ? { card_index: cardIdx } : {}),
    ...(options.sourceField ? { source_field: String(options.sourceField) } : {}),
  };
  try {
    const { getUserDetails } = await import("../services/authService");
    const uid = getUserDetails?.()?.id;
    if (uid != null) body.agentId = uid;
  } catch {
    /* ignore */
  }
  const axRes = await axios.post(ApiReqBaseUrl + "/getDynamicPaymentUrl", body, {
    headers: { "Content-Type": "application/json" },
    validateStatus: () => true,
    withCredentials: true,
  });
  return axiosToFetchLikeResponse(axRes);
};

const removeLdsPaymentLink = async (paymentLink, orderId) => {
  const axRes = await axios.post(
    ApiReqBaseUrl + "/removeLDSPaymentUrl",
    {
      paymentLink: paymentLink.url,
      orderId,
    },
    {
      headers: { "Content-Type": "application/json" },
      validateStatus: () => true,
    }
  );
  return axiosToFetchLikeResponse(axRes);
};

const loadInterestTable = async () => {
  return performGetFetch(`${ApiReqBaseUrl}/loadInterestTable`);
};

const saveInterestTable = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/saveInterestTable`, data);
};

const loadGlobalConstant = async () => {
  const sessionID = await getSessionId();
  const params =
    sessionID && String(sessionID).trim()
      ? { sessionID: String(sessionID).trim() }
      : {};
  return performGetFetch(`${ApiReqBaseUrl}/loadGlobalConstant`, params);
};

const saveGlobalConstant = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/saveGlobalConstant`, data);
};

const loadLDSSuppliers = async () => {
  const sessionID = await getSessionId();
  const params =
    sessionID && String(sessionID).trim()
      ? { sessionID: String(sessionID).trim() }
      : {};
  return performGetFetch(`${ApiReqBaseUrl}/loadLDSSuppliers`, params);
};

const loadAirlines = async () => {
  return performGetFetch(`${ApiReqBaseUrl}/loadAirlines`);
};

const saveLDSSuppliers = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/saveLDSSuppliers`, data);
};

const saveAirlines = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/saveAirlines`, data);
};

/** Customer links + LDS drafts (dashboard / CustomerLinksTable) */
const getCustomerLinks = async (data) => {
  try {
    const base =
      data != null && typeof data === "object" && !Array.isArray(data)
        ? data
        : {};
    const body = _attachAgentSessionToBody(base);
    return await axios.post(`${ApiReqBaseUrl}/api/getCustomerLinks`, body);
  } catch (err) {
    return err.response ?? null;
  }
};

const updateCustomerLinkOrdersCount = async (data) => {
  try {
    return await axios.post(
      `${ApiReqBaseUrl}/api/updateCustomerLinkOrdersCount`,
      data
    );
  } catch (err) {
    return err.response ?? null;
  }
};

const deleteCustomerLink = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/api/deleteCustomerLink`, data);
  } catch (err) {
    return err.response ?? null;
  }
};

const getLdsDrafts = async ({
  sessionID,
  agent_id,
  limit = 50,
  offset = 0,
  include_cancelled,
  include_order_response,
  include_package_compare,
  include_hotel_offer_drafts,
  count_only,
}) => {
  try {
    const body = {
      sessionID,
      agent_id: agent_id ?? null,
      limit,
      offset,
    };
    if (count_only === true) body.count_only = true;
    if (include_cancelled === true) body.include_cancelled = true;
    if (include_order_response === true) body.include_order_response = true;
    if (include_package_compare === false) body.include_package_compare = false;
    if (include_hotel_offer_drafts === false)
      body.include_hotel_offer_drafts = false;
    traceDashboardHeavyApiCall({
      endpoint: "/api/lds-drafts",
      reason: count_only === true ? "getLdsDrafts→count_only" : "getLdsDrafts→list",
    });
    return await axios.post(`${ApiReqBaseUrl}/api/lds-drafts`, body);
  } catch (err) {
    return err.response ?? null;
  }
};

const deleteLdsDraft = async (orderId, sessionID) => {
  try {
    return await axios.delete(
      `${ApiReqBaseUrl}/api/lds-drafts/${encodeURIComponent(orderId)}`,
      {
        data: { sessionID },
        headers: { "Content-Type": "application/json" },
      }
    );
  } catch (err) {
    return err.response ?? null;
  }
};

const updateLdsDraftStatus = async (orderId, sessionID, status) => {
  try {
    return await axios.patch(
      `${ApiReqBaseUrl}/api/lds-drafts/${encodeURIComponent(orderId)}/status`,
      { sessionID, status },
      { headers: { "Content-Type": "application/json" } }
    );
  } catch (err) {
    return err.response ?? null;
  }
};

/** Cruise orders (cruise_routes blueprint, root paths) */
const createCruiseOrder = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/createCruiseOrder`, data);
  } catch (err) {
    return err.response ?? null;
  }
};

const getCruiseOrder = async ({ id, agentID }) => {
  try {
    const params = new URLSearchParams();
    if (agentID != null && agentID !== "") {
      params.set("agentID", String(agentID));
    }
    const sid = _resolveWalletSessionIdSync();
    if (sid) {
      params.set("sessionID", sid);
    }
    const q = params.toString();
    const url = `${ApiReqBaseUrl}/getCruiseOrder/${encodeURIComponent(id)}${
      q ? `?${q}` : ""
    }`;
    return await axios.get(
      url,
      sid
        ? {
            headers: {
              Authorization: `Bearer ${sid}`,
              "X-Session-ID": sid,
            },
          }
        : {}
    );
  } catch (err) {
    return err.response ?? null;
  }
};

const markCruiseOrderPaidManual = async (id, data = {}) => {
  try {
    const body = _attachAgentSessionToBody(data);
    const sid = body?.sessionID;
    return await axios.post(
      `${ApiReqBaseUrl}/markCruiseOrderPaidManual/${encodeURIComponent(id)}`,
      body,
      sid
        ? {
            headers: {
              Authorization: `Bearer ${sid}`,
              "X-Session-ID": sid,
            },
          }
        : {}
    );
  } catch (err) {
    return err.response ?? null;
  }
};

const updateCruiseOrder = async (id, data = {}) => {
  try {
    const body = _attachAgentSessionToBody(data);
    const sid = body?.sessionID;
    return await axios.post(
      `${ApiReqBaseUrl}/updateCruiseOrder/${encodeURIComponent(id)}`,
      body,
      sid
        ? {
            headers: {
              Authorization: `Bearer ${sid}`,
              "X-Session-ID": sid,
            },
          }
        : {}
    );
  } catch (err) {
    return err.response ?? null;
  }
};

const getCruiseOrders = async (data = {}) => {
  try {
    let sid = null;
    try {
      sid = await getSessionId();
    } catch {
      sid = null;
    }
    const sidTrim = sid != null && String(sid).trim() !== "" ? String(sid).trim() : null;
    const bodyWithSession = sidTrim
      ? { ...data, sessionID: sidTrim }
      : _attachAgentSessionToBody(data);
    const axiosExtra = sidTrim
      ? {
          headers: {
            Authorization: `Bearer ${sidTrim}`,
            "X-Session-ID": sidTrim,
          },
        }
      : {};
    return await axios.post(
      `${ApiReqBaseUrl}/getCruiseOrdersByAgentId`,
      bodyWithSession,
      axiosExtra
    );
  } catch (err) {
    return err.response ?? { data: { orders: [], total: 0 } };
  }
};

const getTripGuarantyQuote = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/getTripguarantyQuote`, data);
  } catch (err) {
    return err.response ?? null;
  }
};

/** Public catalog for registration (BASIC / PRO); always under /api so Vite/React Router does not swallow the request. */
const getAgentPackages = async () => {
  try {
    const url = resolveApiUrl("/api/getAgentPackages");
    console.log("Calling getAgentPackages URL:", url);
    return await axios.get(url);
  } catch (err) {
    return err.response ?? { data: null };
  }
};

const getAllAgentPackages = async () => {
  try {
    return await axios.get(`${ApiReqBaseUrl}/getAllAgentPackages`);
  } catch (err) {
    return err.response ?? { data: { packages: [] } };
  }
};

const createAgentPackage = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/createAgentPackage`, data);
  } catch (err) {
    return err.response ?? { data: { error: err?.message } };
  }
};

const updateAgentPackage = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/updateAgentPackage`, data);
  } catch (err) {
    return err.response ?? { data: { error: err?.message } };
  }
};

const deleteAgentPackage = async (data) => {
  try {
    return await axios.post(`${ApiReqBaseUrl}/deleteAgentPackage`, data);
  } catch (err) {
    return err.response ?? { data: { error: err?.message } };
  }
};

const loadAirports = async () => {
  return performGetFetch(`${ApiReqBaseUrl}/loadAirports`);
};

const saveAirports = async (data) => {
  return performPostFetch(`${ApiReqBaseUrl}/saveAirports`, data);
};

const manoReconHeaders = () => {
  const headers = {};
  try {
    const sid = typeof localStorage !== "undefined" ? localStorage.getItem("sessionID") : null;
    if (sid && String(sid).trim() !== "") {
      const token = String(sid).trim();
      headers.Authorization = `Bearer ${token}`;
      headers["X-Session-ID"] = token;
    }
  } catch (_) {
    // Ignore localStorage access errors.
  }
  return headers;
};

const importManoReconciliationReport = async ({
  file,
  agentId,
  reportMonth,
  reportName,
  euroRate,
  advancesTotal,
  financialNotes,
}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("agent_id", String(agentId));
  formData.append("report_month", String(reportMonth));
  formData.append("report_name", String(reportName));
  if (euroRate != null && String(euroRate).trim() !== "") {
    formData.append("euro_rate", String(euroRate));
  }
  if (advancesTotal != null && String(advancesTotal).trim() !== "") {
    formData.append("advances_total", String(advancesTotal));
  }
  if (financialNotes != null && String(financialNotes).trim() !== "") {
    formData.append("financial_notes", String(financialNotes));
  }
  const url = resolveApiUrl("/api/admin/mano-reconciliation/import");
  return axios.post(url, formData, {
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const getAdminManoReconciliationImports = async (params = {}) => {
  const url = resolveApiUrl("/api/admin/mano-reconciliation/imports");
  return axios.get(url, {
    params,
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const getAdminManoReconciliationImportDetails = async (importId) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/imports/${encodeURIComponent(importId)}`
  );
  return axios.get(url, {
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const deleteAdminManoReconciliationImport = async (importId) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/imports/${encodeURIComponent(importId)}`
  );
  return axios.delete(url, {
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const updateAdminManoReconciliationFinancialSummary = async (importId, payload) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/imports/${encodeURIComponent(importId)}/financial-summary`
  );
  return axios.patch(url, payload, {
    headers: { ...manoReconHeaders(), "Content-Type": "application/json" },
    withCredentials: true,
  });
};

const getAdminManoReconciliationAdvances = async (importId) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/imports/${encodeURIComponent(importId)}/advances`
  );
  return axios.get(url, {
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const createAdminManoReconciliationAdvance = async (importId, payload) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/imports/${encodeURIComponent(importId)}/advances`
  );
  return axios.post(url, payload, {
    headers: { ...manoReconHeaders(), "Content-Type": "application/json" },
    withCredentials: true,
  });
};

const updateAdminManoReconciliationAdvance = async (advanceId, payload) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/advances/${encodeURIComponent(advanceId)}`
  );
  return axios.patch(url, payload, {
    headers: { ...manoReconHeaders(), "Content-Type": "application/json" },
    withCredentials: true,
  });
};

const deleteAdminManoReconciliationAdvance = async (advanceId) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/advances/${encodeURIComponent(advanceId)}`
  );
  return axios.delete(url, {
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const updateManoReconciliationRow = async (rowId, payload) => {
  const url = resolveApiUrl(
    `/api/admin/mano-reconciliation/rows/${encodeURIComponent(rowId)}`
  );
  return axios.patch(url, payload, {
    headers: { ...manoReconHeaders(), "Content-Type": "application/json" },
    withCredentials: true,
  });
};

const getAgentManoReconciliationImports = async (params = {}) => {
  const url = resolveApiUrl("/api/mano-reconciliation/imports");
  return axios.get(url, {
    params,
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

const getAgentManoReconciliationImportDetails = async (importId, params = {}) => {
  const url = resolveApiUrl(`/api/mano-reconciliation/imports/${encodeURIComponent(importId)}`);
  return axios.get(url, {
    params,
    headers: manoReconHeaders(),
    withCredentials: true,
  });
};

/** System-wide HR commission totals (עמלות HR) — agent 5413 KPI only. */
const getHrCommissionSystemTotal = async ({ month, year } = {}) => {
  try {
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Accept: "application/json; charset=utf-8",
      ..._walletBearerHeaders(),
    };
    const payload = {};
    if (month != null && String(month).trim() !== "") {
      payload.month = String(month).trim();
    }
    if (year != null && String(year).trim() !== "") {
      payload.year = Number(year);
    }
    const body = _attachAgentSessionToBody(payload);
    const res = await axios.post(
      resolveApiUrl("/getHrCommissionSystemTotal"),
      body,
      { headers, withCredentials: true }
    );
    const data = res?.data && typeof res.data === "object" ? res.data : {};
    const monthIls = Number(data.monthIls ?? data.totalIls);
    const yearIls = Number(data.yearIls);
    return {
      ...res,
      status: res?.status,
      data: {
        success: data.success !== false,
        month: data.month || payload.month || null,
        year: data.year != null ? Number(data.year) : payload.year || null,
        monthIls: Number.isFinite(monthIls) ? monthIls : 0,
        yearIls: Number.isFinite(yearIls) ? yearIls : 0,
        totalIls: Number.isFinite(monthIls) ? monthIls : 0,
        hotelsIls: Number(data.hotelsIls) || 0,
        ldsIls: Number(data.ldsIls) || 0,
        campaignIls: Number(data.campaignIls) || 0,
        baggageMarkupIls: Number(data.baggageMarkupIls) || 0,
        currency: data.currency || "ILS",
      },
    };
  } catch (err) {
    console.warn("[getHrCommissionSystemTotal]", {
      message: err?.message,
      status: err?.response?.status,
      body: err?.response?.data,
    });
    return {
      status: err?.response?.status || 0,
      data: {
        success: false,
        month: null,
        year: null,
        monthIls: 0,
        yearIls: 0,
        totalIls: 0,
        hotelsIls: 0,
        ldsIls: 0,
        campaignIls: 0,
        baggageMarkupIls: 0,
        currency: "ILS",
      },
    };
  }
};

/** Per-order HR commission breakdown for a calendar month — agent 5413 KPI only. */
const getHrCommissionSystemOrders = async ({ month, year } = {}) => {
  try {
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Accept: "application/json; charset=utf-8",
      ..._walletBearerHeaders(),
    };
    const payload = {};
    if (month != null && String(month).trim() !== "") {
      payload.month = String(month).trim();
    }
    if (year != null && String(year).trim() !== "") {
      payload.year = Number(year);
    }
    const body = _attachAgentSessionToBody(payload);
    const res = await axios.post(
      resolveApiUrl("/getHrCommissionSystemOrders"),
      body,
      { headers, withCredentials: true }
    );
    const data = res?.data && typeof res.data === "object" ? res.data : {};
    const orders = Array.isArray(data.orders) ? data.orders : [];
    const monthIls = Number(data.monthIls ?? data.totalIls);
    return {
      ...res,
      status: res?.status,
      data: {
        success: data.success !== false,
        month: data.month || payload.month || null,
        year: data.year != null ? Number(data.year) : payload.year || null,
        monthIls: Number.isFinite(monthIls) ? monthIls : 0,
        totalIls: Number.isFinite(monthIls) ? monthIls : 0,
        hotelsIls: Number(data.hotelsIls) || 0,
        ldsIls: Number(data.ldsIls) || 0,
        campaignIls: Number(data.campaignIls) || 0,
        baggageMarkupIls: Number(data.baggageMarkupIls) || 0,
        baggageOrderCount: Number(data.baggageOrderCount) || 0,
        baggageLineCount: Number(data.baggageLineCount) || 0,
        orderCount: Number(data.orderCount) || orders.length,
        currency: data.currency || "ILS",
        orders,
      },
    };
  } catch (err) {
    console.warn("[getHrCommissionSystemOrders]", {
      message: err?.message,
      status: err?.response?.status,
      body: err?.response?.data,
    });
    return {
      status: err?.response?.status || 0,
      data: {
        success: false,
        month: null,
        year: null,
        monthIls: 0,
        totalIls: 0,
        hotelsIls: 0,
        ldsIls: 0,
        campaignIls: 0,
        baggageMarkupIls: 0,
        baggageOrderCount: 0,
        baggageLineCount: 0,
        orderCount: 0,
        currency: "ILS",
        orders: [],
      },
    };
  }
};

/** Soft-exclude one HR commission row from KPI/list — agent 5413 admin only. */
const excludeHrCommissionSystemOrder = async ({
  source,
  orderId,
  reason,
} = {}) => {
  try {
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Accept: "application/json; charset=utf-8",
      ..._walletBearerHeaders(),
    };
    const payload = {
      source: String(source || "").trim(),
      orderId: String(orderId || "").trim(),
    };
    if (reason != null && String(reason).trim() !== "") {
      payload.reason = String(reason).trim();
    }
    const body = _attachAgentSessionToBody(payload);
    const res = await axios.post(
      resolveApiUrl("/excludeHrCommissionSystemOrder"),
      body,
      { headers, withCredentials: true }
    );
    const data = res?.data && typeof res.data === "object" ? res.data : {};
    return {
      ...res,
      status: res?.status,
      data: {
        success: data.success !== false,
        source: data.source || payload.source,
        orderId: data.orderId || payload.orderId,
        excluded: Boolean(data.excluded),
        error: data.error || null,
      },
    };
  } catch (err) {
    console.warn("[excludeHrCommissionSystemOrder]", {
      message: err?.message,
      status: err?.response?.status,
      body: err?.response?.data,
    });
    return {
      status: err?.response?.status || 0,
      data: {
        success: false,
        source: String(source || "").trim() || null,
        orderId: String(orderId || "").trim() || null,
        excluded: false,
        error: err?.response?.data?.error || err?.message || "request_failed",
      },
    };
  }
};

/** Global recent-orders feed for dashboard home (all agents; session auth). */
const getRecentOrdersAll = async ({ limit = 50 } = {}) => {
  const capped = Math.max(1, Math.min(Number(limit) || 50, 200));
  return performPostFetch(
    resolveApiUrl("/getRecentOrdersAll"),
    _attachAgentSessionToBody({ limit: capped }),
    false,
    { headers: _walletBearerHeaders() }
  );
};

export {
  loadAirlines,
  saveAirlines,
  loadAirports,
  saveAirports,
  removeLdsPaymentLink,
  searchHotels,
  searchHotelsComposite,
  validateCompositeHotelQuote,
  searchHotelsBasis,
  createLdsPaymentLink,
  createLdsOrderDetailsPaymentLink,
  fetchPackageOrderPaymentLinks,
  deletePackageOrderPaymentLink,
  patchAdminOrderCommission,
  createDynamicPaymentLink,
  getHotelsProfileData,
  getFinanceData,
  getOrders,
  getHotelsImage,
  getHotelsProfileImages,
  getHotelsDescription,
  bookHotel,
  bookCompositeHotel,
  newHotelOrder,
  getDelayedPayOrder,
  delayedPayment,
  checkInInfo,
  cancellationPolicies,
  validateHotelPackageBeforeContinue,
  dynamicPackageRoomsPrecheck,
  getFacilities,
  pay,
  getAdminHotelOrders,
  getOrdersByEmail,
  sendSuccessOrder,
  getFinancesByEmail,
  createFinance,
  approveFinanceRequest,
  declineFinanceRequest,
  setFinanceRequestComments,
  getAllUsersEmail,
  getAllAgentsBalanceSummary,
  getAllAgentsFutureBalanceSummary,
  getAllOrdersWithFinanceDetails,
  getAllOrdersWithFinanceDetailsWithProgress,
  getOrdersSinceLastUpdate,
  getRecentOrdersAll,
  getHrCommissionSystemTotal,
  getHrCommissionSystemOrders,
  excludeHrCommissionSystemOrder,
  getAgents,
  getAgentById,
  getFeaturedPackagesSettings,
  normalizeFeaturedPackagesSettingsResponse,
  getAllAgentsWithFinancialData,
  getAgentRecommendedByCount,
  updateOrderFinancials,
  searchLds,
  getLdsDates,
  getActiveDestinations,
  getHotelInfo,
  getLdsOrder,
  fetchUnifiedOrderById,
  getLDSOrderById,
  createLdsNewOrder,
  findExtendedDeals,
  fetchVpCanonicalPricing,
  getPackageFlightLinks,
  updateCurrency,
  payLds,
  updateLdsHandlingfee,
  getLDSOrders,
  mergeLdsOrdersProposal,
  previewMergeLdsOrdersProposal,
  activateAgent,
  loadInterestTable,
  saveInterestTable,
  loadGlobalConstant,
  saveGlobalConstant,
  updateLdsOrder,
  ensureLdsBankTransferConfirmation,
  transferOrderAgent,
  getOrgTourInfo,
  saveLDSSuppliers,
  loadLDSSuppliers,
  getFutureOrdersCommission,
  searchFlights,
  getCustomerLinks,
  updateCustomerLinkOrdersCount,
  deleteCustomerLink,
  getLdsDrafts,
  deleteLdsDraft,
  updateLdsDraftStatus,
  createCruiseOrder,
  getCruiseOrder,
  updateCruiseOrder,
  markCruiseOrderPaidManual,
  getCruiseOrders,
  getTripGuarantyQuote,
  getAgentPackages,
  getAllAgentPackages,
  createAgentPackage,
  updateAgentPackage,
  deleteAgentPackage,
  importManoReconciliationReport,
  getAdminManoReconciliationImports,
  getAdminManoReconciliationImportDetails,
  deleteAdminManoReconciliationImport,
  updateAdminManoReconciliationFinancialSummary,
  getAdminManoReconciliationAdvances,
  createAdminManoReconciliationAdvance,
  updateAdminManoReconciliationAdvance,
  deleteAdminManoReconciliationAdvance,
  updateManoReconciliationRow,
  getAgentManoReconciliationImports,
  getAgentManoReconciliationImportDetails,
  resolveAgentPostSessionIdSync,
};
